import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  ArrowLeft, Download, Edit, FileText,
  Briefcase, Award, GraduationCap, User,
  ChevronRight, ExternalLink, Mail, Phone,
  Linkedin, Github, Eye, Twitter, Video,
  DollarSign, CheckCircle, XCircle, Save, X,
  Plus, Trash2, Calendar, Copy, Send, Users, Clock,
  Shield, MapPin, Search, Sparkles, Globe
} from 'lucide-react';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { formatDate, formatFileSize, formatMonthYear } from '../utils/mockData';
import '../styles/Dashboard.css';
import BASE_URL from '../../url';
import Swal from 'sweetalert2';
import { countriesData, getStatesForCountry, getStateCode, stateMapping } from '../../erprecruitment/config/locationConfig';
import { formatName, formatJobTitle, formatFullName, formatNameFields } from '../utils/nameFormatter';
import { SubmitProfileModal } from './SubmitProfileModal';

const CURRENCY_MAP = {
  IN: { symbol: '₹', label: 'INR' },
  US: { symbol: '$', label: 'USD' },
  GB: { symbol: '£', label: 'GBP' },
  CA: { symbol: 'C$', label: 'CAD' },
  AU: { symbol: 'A$', label: 'AUD' },
};

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  };
};

// Helper function to remove duplicates from an array based on a key function
const removeDuplicates = (array, keyFn) => {
  const seen = new Set();
  return array.filter(item => {
    const key = keyFn(item);
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
};

const NOTE_CATEGORY_COLORS = {
  GENERAL: { bg: '#f3f4f6', color: '#1f2937', border: '#e5e7eb' },
  CALL_LOG: { bg: '#eff6ff', color: '#1d4ed8', border: '#bfdbfe' },
  EMAIL_LOG: { bg: '#f5f3ff', color: '#6d28d9', border: '#ddd6fe' },
  INTERVIEW_FEEDBACK: { bg: '#ecfdf5', color: '#047857', border: '#a7f3d0' },
  COMPLIANCE: { bg: '#fffbeb', color: '#b45309', border: '#fde68a' },
  PLACEMENT: { bg: '#fff7ed', color: '#c2410c', border: '#ffedd5' },
  FOLLOW_UP: { bg: '#fdf2f8', color: '#be185d', border: '#fbcfe8' },
  INTERNAL: { bg: '#fafafa', color: '#4b5563', border: '#e5e7eb' }
};

const NOTE_CATEGORY_LABELS = {
  GENERAL: 'General',
  CALL_LOG: 'Call Log',
  EMAIL_LOG: 'Email Log',
  INTERVIEW_FEEDBACK: 'Interview Feedback',
  COMPLIANCE: 'Compliance',
  PLACEMENT: 'Placement',
  FOLLOW_UP: 'Follow Up',
  INTERNAL: 'Internal'
};

// Format phone: ensure '+' prefix for international numbers
const formatPhone = (phone) => {
  if (!phone) return null;
  const cleaned = phone.toString().trim();
  if (!cleaned) return null;
  return cleaned.startsWith('+') ? cleaned : `+${cleaned}`;
};

const truncateText = (text, limit = 30) => {
  if (!text) return '';
  return text.length > limit ? text.substring(0, limit) + '...' : text;
};

// This transform function matches your backend response structure
const transformCandidate = (candidate) => {
  console.log('Raw candidate from API:', candidate);

  return {
    CandidateId: candidate.candidate_id,
    CandidateCode: candidate.candidate_code,
    EmployeeCode: candidate.employee_id || '',
    FirstName: formatName(candidate.first_name || ''),
    LastName: formatName(candidate.last_name || ''),
    MiddleName: formatName(candidate.middle_name || ''),
    JobTitle: formatJobTitle(candidate.job_title || ''),
    YearsOfExperience: candidate.years_exp,
    CandidateStatus: candidate.status || 'Available',
    RemoteStatus: candidate.remote_status || 'Remote',
    CreatedDt: candidate.created_on,
    UpdatedDt: candidate.modified_on || '',
    ProfileSummary: candidate.profile_summary || '',
    LinkedInUrl: candidate.linkedin_url || '',
    GitHubUrl: candidate.github_url || '',
    TwitterUrl: candidate.twitter_url || '',
    VideoResumeUrl: candidate.video_resume_url || '',
    Gender: candidate.gender || '',
    CurrentLocation: candidate.current_location || '',
    // Raw location parts from DB (for edit mode - avoids fragile string parsing)
    LocationCity: candidate.city_name || candidate.CityName || '',
    LocationStateCode: candidate.state_code || candidate.StateCode || '',
    LocationCountryIso2: candidate.country_iso2 || candidate.CountryIso2 || '',
    CreatedBy: candidate.created_by || '',

    // NEW FIELDS
    Mobile: candidate.mobile || candidate.phone || '',
    WorkAuthorization: candidate.work_authorization || '',
    EmploymentType: candidate.employment_type || candidate.EmploymentType || candidate.employmentType || '',
    SecurityClearance: candidate.security_clearance || '',
    WillingToRelocate: candidate.willing_to_relocate || false,
    IsBench: candidate.is_bench || false,
    ExpectedRateFrom: candidate.expected_rate_from || '',
    ExpectedRateTo: candidate.expected_rate_to || '',
    ExpectedRateType: candidate.expected_rate_type || 'Hourly',
    CurrentRate: candidate.current_rate || '',
    CurrentRateType: candidate.current_rate_type || 'Hourly',
    MaritalStatus: candidate.marital_status || '',
    Industry: candidate.industry || '',
    CurrentEmployer: candidate.current_employer || '',
    CurrentSalary: candidate.current_salary || '',
    ExpectedSalary: candidate.expected_salary || '',
    NoticePeriod: candidate.notice_period || '',

    // Bench Fields
    BenchPriority: candidate.bench_profile?.PriorityCode || candidate.PriorityCode || candidate.BenchPriority || '',
    OutOfJobSince: candidate.bench_profile?.OutOfJobSince || candidate.OutOfJobSince || '',
    DaysOnBench: candidate.DaysOnBench !== undefined ? candidate.DaysOnBench : (candidate.days_on_bench !== undefined ? candidate.days_on_bench : candidate.bench_profile?.DaysOnBench),
    NewJobDeadline: candidate.bench_profile?.NewJobDeadline || candidate.NewJobDeadline || '',
    RecentPlacementDate: candidate.bench_profile?.RecentPlacementDate || candidate.RecentPlacementDate || '',
    WorkAuthPetitioner: candidate.bench_profile?.WorkAuthPetitioner || candidate.WorkAuthPetitioner || '',
    WorkAuthExpiryDate: candidate.bench_profile?.WorkAuthExpiryDate || candidate.WorkAuthExpiryDate || '',
    BenchAssignees: (candidate.bench_assignees && candidate.bench_assignees.length > 0)
      ? candidate.bench_assignees.map(a => ({
        UserId: String(a.UserId || ''),
        UserName: a.UserName || '',
        IsPrimary: a.IsPrimary ? 1 : 0
      }))
      : (candidate.PrimaryAssignee ? [{ UserId: String(candidate.PrimaryAssigneeUserId || ''), UserName: candidate.PrimaryAssignee, IsPrimary: 1 }] : []),
    BenchAssignee: (candidate.bench_assignees && candidate.bench_assignees.length > 0)
      ? (candidate.bench_assignees.find(a => a.IsPrimary) || candidate.bench_assignees[0])?.UserName || ''
      : (candidate.PrimaryAssignee || ''),
    BenchAssigneeUserId: (candidate.bench_assignees && candidate.bench_assignees.length > 0)
      ? (candidate.bench_assignees.find(a => a.IsPrimary) || candidate.bench_assignees[0])?.UserId || ''
      : (candidate.PrimaryAssigneeUserId || ''),
    CurrentClientName: candidate.current_client_name || candidate.CurrentClientName || candidate.bench_profile?.CurrentClientName || '',
    CurrentEmployer: candidate.current_employer || candidate.CurrentEmployer || candidate.bench_profile?.CurrentEmployer || '',
    ReasonForLeaving: candidate.reason_for_leaving || candidate.ReasonForLeaving || candidate.bench_profile?.ReasonForLeaving || '',
    ReferenceText: candidate.bench_profile?.ReferenceText || candidate.ReferenceText || '',
    Remarks: candidate.bench_profile?.Remarks || candidate.Remarks || '',
    H1BTransferWilling: candidate.bench_profile?.H1BTransferWilling ?? candidate.H1BTransferWilling ?? false,
    I140Status: candidate.bench_profile?.I140Status || candidate.I140Status || 'NA',
    I140PriorityDate: candidate.bench_profile?.I140PriorityDate || candidate.I140PriorityDate || '',
    C2CCorpName: candidate.bench_profile?.C2CCorpName || candidate.C2CCorpName || '',
    C2CFEIN: candidate.bench_profile?.C2CFEIN || candidate.C2CFEIN || '',
    C2CStructure: candidate.bench_profile?.C2CStructure || candidate.C2CStructure || '',
    WillingToTravel: candidate.willing_to_travel ?? candidate.WillingToTravel ?? false,
    AvailableFromDate: candidate.available_from_date || candidate.AvailableFromDate || '',
    RelocationNotes: candidate.relocation_notes || candidate.RelocationNotes || '',
    IsCurrentlyWorking: candidate.is_currently_working ?? candidate.IsCurrentlyWorking ?? false,

    Contact: {
      Email: candidate.email || '',
      // Only show Phone separately if the DB has BOTH a real mobile AND a different phone
      Phone: (candidate.mobile && candidate.phone && candidate.phone !== candidate.mobile) ? candidate.phone : '',
      Mobile: candidate.mobile || candidate.phone || '',
    },
    Skills: removeDuplicates(
      (candidate.skills || []).map(s => ({
        SkillName: s.skill_name || '',
        SkillType: 'HARD'
      })),
      (skill) => skill.SkillName.toLowerCase()
    ),
    Education: (candidate.education || []).map(e => ({
      EducationId: e.EducationId,
      Institution: formatName(e.Institution || e.institution || ''),
      Degree: formatName(e.Degree || e.degree || ''),
      FieldOfStudy: e.FieldOfStudy || e.fieldOfStudy || '',
      StartDate: e.StartDate || e.startDate || '',
      EndDate: e.EndDate || e.endDate || '',
      GPA: e.GPA || e.gpa || '',
    })),
    WorkExperience: (candidate.work_experience || []).map(w => ({
      WorkExperienceId: w.WorkExperienceId,
      Company: w.Company || w.company || '',
      JobTitle: formatJobTitle(w.JobTitle || w.jobTitle || ''),
      StartDate: w.StartDate || w.startDate || '',
      EndDate: w.EndDate || w.endDate || '',
      IsCurrent: w.IsCurrent ?? w.isCurrent ?? false,
      Description: w.Description || w.description || '',
    })),
    Certifications: (candidate.certifications || []).map(c => ({
      CertificationId: c.CertificationId,
      CertificationName: formatName(c.CertificationName || c.name || ''),
      IssuingOrganization: c.IssuingOrganization || c.issuingOrg || '',
      IssueDate: c.IssueDate || c.issueDate || '',
      ExpiryDate: c.ExpiryDate || c.expiryDate || '',
      CredentialId: c.CredentialId || c.credentialId || '',
      CredentialUrl: c.CredentialUrl || c.credentialUrl || '',
    })),
    Document: candidate.document ? {
      DocumentId: candidate.document.DocumentId,
      FileNameOriginal: candidate.document.FileNameOriginal,
      FileExtension: candidate.document.FileExtension,
      MimeType: candidate.document.MimeType,
      FileSizeBytes: candidate.document.FileSizeBytes,
      StorageLocator: candidate.document.StorageLocator,
      ParseStatus: candidate.document.ParseStatus,
      DownloadUrl: `${BASE_URL}/api/resumes/${candidate.document.DocumentId}/download`,
      PreviewUrl: `${BASE_URL}/api/resumes/${candidate.document.DocumentId}/preview`,
      IsPrimaryResume: candidate.document.IsPrimaryResume,
    } : null,
    Documents: (candidate.documents || []).map(doc => ({
      DocumentId: doc.DocumentId,
      DocumentType: doc.DocumentType,
      FileNameOriginal: doc.FileNameOriginal,
      FileExtension: doc.FileExtension,
      MimeType: doc.MimeType,
      FileSizeBytes: doc.FileSizeBytes,
      IsPrimaryResume: doc.IsPrimaryResume,
      UploadedOn: doc.UploadedOn,
      DocumentName: doc.DocumentName || doc.FileNameOriginal,
    })),
  };
};

const STATUS_COLORS = {
  'Available': { bg: '#d1fae5', color: '#065f46' },
  'In Process': { bg: '#fef3c7', color: '#92400e' },
  'Hired': { bg: '#dbeafe', color: '#1d4ed8' },
  'Not Available': { bg: '#fee2e2', color: '#991b1b' },
  'On Hold': { bg: '#f3f4f6', color: '#4b5563' },
};

const REMOTE_COLORS = {
  'Remote': { bg: '#ede9fe', color: '#5b21b6' },
  'OnSite': { bg: '#e0f2fe', color: '#0369a1' },
  'Hybrid': { bg: '#fef9c3', color: '#713f12' },
};

const SKILL_TYPE_COLORS = {
  'HARD': { bg: '#e0f2fe', color: '#0369a1' },
  'SOFT': { bg: '#fce7f3', color: '#9d174d' },
  'Expert': { bg: '#f3e8ff', color: '#6b21a8' },
  'Adv': { bg: '#ecfdf5', color: '#047857' },
  'Intermediate': { bg: '#e0f2fe', color: '#0369a1' },
  'Beginner': { bg: '#fef3c7', color: '#b45309' },
};

const BENCH_PRIORITY_COLORS = {
  'CRITICAL': { bg: '#fee2e2', color: '#991b1b', label: 'Critical' },
  'HIGH': { bg: '#fee2e2', color: '#b91c1c', label: 'High' },
  'MEDIUM': { bg: '#fef3c7', color: '#92400e', label: 'Medium' },
  'LOW': { bg: '#d1fae5', color: '#065f46', label: 'Low' },
};

const getBenchAgingStatus = (outOfJobSince, createdDt, apiDaysOnBench) => {
  let days = null;
  if (apiDaysOnBench !== undefined && apiDaysOnBench !== null && !isNaN(Number(apiDaysOnBench))) {
    days = Math.max(0, Number(apiDaysOnBench));
  } else {
    const dateStr = outOfJobSince || createdDt;
    if (!dateStr) return { days: 0, label: 'Fresh', bg: '#d1fae5', color: '#065f46', icon: '🟢' };
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return { days: 0, label: 'Fresh', bg: '#d1fae5', color: '#065f46', icon: '🟢' };

    // Normalize both dates to calendar day midnight (matches SQL DATEDIFF(DAY, ...))
    const now = new Date();
    const utcNow = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
    const utcD = Date.UTC(d.getFullYear(), d.getMonth(), d.getDate());
    days = Math.max(0, Math.floor((utcNow - utcD) / (1000 * 60 * 60 * 24)));
  }

  if (days >= 46) {
    return { days, label: 'Critical', bg: '#fee2e2', color: '#991b1b', icon: '🔴' };
  } else if (days >= 31) {
    return { days, label: 'Hot', bg: '#ffedd5', color: '#c2410c', icon: '🟠' };
  } else if (days >= 16) {
    return { days, label: 'Warming', bg: '#fef3c7', color: '#92400e', icon: '🟡' };
  } else {
    return { days, label: 'Fresh', bg: '#d1fae5', color: '#065f46', icon: '🟢' };
  }
};

const AVATAR_COLORS = [
  '#3b5bfd', '#3b82f6', '#8b5cf6', '#ec4899', '#f59e0b',
  '#2563eb', '#6366f1', '#ef4444', '#1d4ed8', '#f97316',
];

const avatarColor = (name) => {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

const getInitials = (first, last) =>
  ((first || '').charAt(0) + (last || '').charAt(0)).toUpperCase() || '?';

// Custom hook for copy functionality
const useCopyToClipboard = () => {
  const showToast = (message) => {
    const toast = document.createElement('div');
    toast.textContent = message;
    toast.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: #3b5bfd;
      color: white;
      padding: 8px 16px;
      border-radius: 4px;
      z-index: 9999;
      animation: fadeOut 2s forwards;
      font-size: 14px;
      box-shadow: 0 2px 8px rgba(0,0,0,0.2);
    `;
    document.body.appendChild(toast);
    setTimeout(() => toast.remove(), 2000);
  };

  const copyToClipboard = (text, label = 'Text') => {
    navigator.clipboard.writeText(text).then(() => {
      showToast(`${label} copied to clipboard!`);
    }).catch(err => {
      console.error('Failed to copy: ', err);
    });
  };

  return copyToClipboard;
};

// Copyable Link Component
const CopyableLink = ({ href, text, icon: Icon, label }) => {
  const copyToClipboard = useCopyToClipboard();
  const safeHref = href && !/^https?:\/\//i.test(href.trim()) ? `https://${href.trim()}` : (href ? href.trim() : '');

  const handleContextMenu = (e) => {
    e.preventDefault();
    copyToClipboard(safeHref, `${label} URL`);
  };

  return (
    <a
      href={safeHref}
      target="_blank"
      rel="noopener noreferrer"
      style={{ color: '#3b5bfd', display: 'inline-flex', alignItems: 'center', gap: '4px', textDecoration: 'none', cursor: 'pointer' }}
      title={`Right-click to copy ${label} URL`}
      onContextMenu={handleContextMenu}
    >
      {Icon && <Icon size={12} />} {text} <ExternalLink size={10} />
    </a>
  );
};

// Copyable Text Component
const CopyableText = ({ text, icon: Icon, label }) => {
  const copyToClipboard = useCopyToClipboard();

  const handleContextMenu = (e) => {
    e.preventDefault();
    copyToClipboard(text, label);
  };

  return (
    <span
      style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}
      title={`Right-click to copy ${label}`}
      onContextMenu={handleContextMenu}
    >
      {Icon && <Icon size={12} />} {text}
    </span>
  );
};

const InfoRow = ({ label, value, isEditing, fieldName, onEdit, options = null, type = 'text', displayValue = null, countryCode = 'us' }) => {
  if (isEditing) {
    if (options) {
      return (
        <div className="candidate-info-row">
          <span className="candidate-info-label">{label}</span>
          <span className="candidate-info-value">
            <select
              value={value || ''}
              onChange={(e) => onEdit(fieldName, e.target.value)}
              className="edit-select"
              style={{
                padding: '4px 8px',
                borderRadius: '4px',
                border: '1px solid #3b5bfd',
                fontSize: '13px',
                width: '100%'
              }}
            >
              <option value="">Select {label}</option>
              {options.map(opt => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </span>
        </div>
      );
    }

    if (type === 'mobile') {
      return (
        <div className="candidate-info-row">
          <span className="candidate-info-label">{label}</span>
          <span className="candidate-info-value">
            <PhoneInput
              country={countryCode ? countryCode.toLowerCase() : 'us'}
              value={value || ''}
              onChange={(phone) => onEdit(fieldName, phone)}
              inputProps={{ name: fieldName, className: 'edit-input', style: { paddingLeft: '48px', width: '100%', height: '36px', border: '1px solid #3b5bfd', borderRadius: '4px', fontSize: '13px' } }}
              containerStyle={{ width: '100%' }}
              buttonStyle={{ background: 'transparent', border: 'none', paddingLeft: '8px' }}
              dropdownStyle={{ width: '300px', zIndex: 1000 }}
            />
          </span>
        </div>
      );
    }

    if (type === 'checkbox') {
      return (
        <div className="candidate-info-row">
          <span className="candidate-info-label">{label}</span>
          <span className="candidate-info-value">
            <input
              type="checkbox"
              checked={value || false}
              onChange={(e) => onEdit(fieldName, e.target.checked)}
              style={{ width: '16px', height: '16px', cursor: 'pointer' }}
            />
          </span>
        </div>
      );
    }

    return (
      <div className="candidate-info-row">
        <span className="candidate-info-label">{label}</span>
        <span className="candidate-info-value">
          <input
            type={type}
            value={value || ''}
            onChange={(e) => onEdit(fieldName, e.target.value)}
            onBlur={(e) => {
              if (fieldName === 'FirstName' || fieldName === 'LastName' || fieldName === 'MiddleName') {
                onEdit(fieldName, formatName(e.target.value));
              } else if (fieldName === 'JobTitle') {
                onEdit(fieldName, formatJobTitle(e.target.value));
              }
            }}
            className="edit-input"
            style={{
              padding: '4px 8px',
              borderRadius: '4px',
              border: '1px solid #3b5bfd',
              fontSize: '13px',
              width: '100%'
            }}
          />
        </span>
      </div>
    );
  }

  return (
    <div className="candidate-info-row">
      <span className="candidate-info-label">{label}</span>
      <span className="candidate-info-value">
        {displayValue || (value != null && value !== '' ? value : <span style={{ color: '#9ca3af' }}>—</span>)}
      </span>
    </div>
  );
};

const EditSection = ({ children, isEditing }) => {
  return isEditing ? (
    <div style={{ border: '1px dashed #3b5bfd', padding: '10px', borderRadius: '8px', marginBottom: '10px' }}>
      {children}
    </div>
  ) : children;
};

// Work Experience Editor Component
const WorkExperienceEditor = ({ experiences, onChange }) => {
  const addExperience = () => {
    onChange([
      ...experiences,
      {
        WorkExperienceId: `temp-${Date.now()}-${experiences.length}`,
        Company: '',
        JobTitle: '',
        StartDate: '',
        EndDate: '',
        IsCurrent: false,
        Description: ''
      }
    ]);
  };

  const updateExperience = (index, field, value) => {
    const updated = [...experiences];
    updated[index] = { ...updated[index], [field]: value };
    onChange(updated);
  };

  const removeExperience = (index) => {
    const updated = experiences.filter((_, i) => i !== index);
    onChange(updated);
  };

  return (
    <div>
      {experiences.map((exp, index) => (
        <div key={exp.WorkExperienceId || index} style={{
          background: '#f8f9fa',
          borderRadius: '8px',
          padding: '16px',
          marginBottom: '16px',
          border: '1px solid #e5e7eb',
          position: 'relative'
        }}>
          <button
            onClick={() => removeExperience(index)}
            style={{
              position: 'absolute',
              top: '8px',
              right: '8px',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: '#ef4444'
            }}
          >
            <Trash2 size={16} />
          </button>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
            <input
              type="text"
              value={exp.Company}
              onChange={(e) => updateExperience(index, 'Company', e.target.value)}
              placeholder="Company Name"
              style={{ padding: '8px', borderRadius: '4px', border: '1px solid #d1d5db' }}
            />
            <input
              type="text"
              value={exp.JobTitle}
              onChange={(e) => updateExperience(index, 'JobTitle', e.target.value)}
              placeholder="Job Title"
              style={{ padding: '8px', borderRadius: '4px', border: '1px solid #d1d5db' }}
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: '12px', marginBottom: '12px' }}>
            <input
              type="month"
              value={exp.StartDate}
              onChange={(e) => updateExperience(index, 'StartDate', e.target.value)}
              placeholder="Start Date"
              style={{ padding: '8px', borderRadius: '4px', border: '1px solid #d1d5db' }}
            />
            <input
              type="month"
              value={exp.EndDate}
              onChange={(e) => updateExperience(index, 'EndDate', e.target.value)}
              placeholder="End Date"
              disabled={exp.IsCurrent}
              style={{ padding: '8px', borderRadius: '4px', border: '1px solid #d1d5db' }}
            />
            <label style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <input
                type="checkbox"
                checked={exp.IsCurrent}
                onChange={(e) => updateExperience(index, 'IsCurrent', e.target.checked)}
              />
              Current
            </label>
          </div>

          <textarea
            value={exp.Description}
            onChange={(e) => updateExperience(index, 'Description', e.target.value)}
            placeholder="Job Description"
            rows={3}
            style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #d1d5db' }}
          />
        </div>
      ))}

      <button
        onClick={addExperience}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          padding: '8px 16px',
          background: '#f3f4f6',
          border: '1px dashed #3b5bfd',
          borderRadius: '4px',
          cursor: 'pointer',
          color: '#3b5bfd'
        }}
      >
        <Plus size={16} /> Add Work Experience
      </button>
    </div>
  );
};

// Education Editor Component
const EducationEditor = ({ educations, onChange }) => {
  const addEducation = () => {
    onChange([
      ...educations,
      {
        EducationId: `temp-${Date.now()}-${educations.length}`,
        Institution: '',
        Degree: '',
        FieldOfStudy: '',
        StartDate: '',
        EndDate: '',
        GPA: ''
      }
    ]);
  };

  const updateEducation = (index, field, value) => {
    const updated = [...educations];
    updated[index] = { ...updated[index], [field]: value };
    onChange(updated);
  };

  const removeEducation = (index) => {
    const updated = educations.filter((_, i) => i !== index);
    onChange(updated);
  };

  return (
    <div>
      {educations.map((edu, index) => (
        <div key={edu.EducationId || index} style={{
          background: '#f8f9fa',
          borderRadius: '8px',
          padding: '16px',
          marginBottom: '16px',
          border: '1px solid #e5e7eb',
          position: 'relative'
        }}>
          <button
            onClick={() => removeEducation(index)}
            style={{
              position: 'absolute',
              top: '8px',
              right: '8px',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: '#ef4444'
            }}
          >
            <Trash2 size={16} />
          </button>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
            <input
              type="text"
              value={edu.Institution}
              onChange={(e) => updateEducation(index, 'Institution', e.target.value)}
              placeholder="Institution"
              style={{ padding: '8px', borderRadius: '4px', border: '1px solid #d1d5db' }}
            />
            <input
              type="text"
              value={edu.Degree}
              onChange={(e) => updateEducation(index, 'Degree', e.target.value)}
              placeholder="Degree"
              style={{ padding: '8px', borderRadius: '4px', border: '1px solid #d1d5db' }}
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
            <input
              type="text"
              value={edu.FieldOfStudy}
              onChange={(e) => updateEducation(index, 'FieldOfStudy', e.target.value)}
              placeholder="Field of Study"
              style={{ padding: '8px', borderRadius: '4px', border: '1px solid #d1d5db' }}
            />
            <input
              type="text"
              value={edu.GPA}
              onChange={(e) => updateEducation(index, 'GPA', e.target.value)}
              placeholder="GPA"
              style={{ padding: '8px', borderRadius: '4px', border: '1px solid #d1d5db' }}
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <input
              type="month"
              value={edu.StartDate}
              onChange={(e) => updateEducation(index, 'StartDate', e.target.value)}
              placeholder="Start Date"
              style={{ padding: '8px', borderRadius: '4px', border: '1px solid #d1d5db' }}
            />
            <input
              type="month"
              value={edu.EndDate}
              onChange={(e) => updateEducation(index, 'EndDate', e.target.value)}
              placeholder="End Date"
              style={{ padding: '8px', borderRadius: '4px', border: '1px solid #d1d5db' }}
            />
          </div>
        </div>
      ))}

      <button
        onClick={addEducation}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          padding: '8px 16px',
          background: '#f3f4f6',
          border: '1px dashed #3b5bfd',
          borderRadius: '4px',
          cursor: 'pointer',
          color: '#3b5bfd'
        }}
      >
        <Plus size={16} /> Add Education
      </button>
    </div>
  );
};

// Certifications Editor Component
const CertificationEditor = ({ certifications, onChange }) => {
  const addCertification = () => {
    onChange([
      ...certifications,
      {
        CertificationId: `temp-${Date.now()}-${certifications.length}`,
        CertificationName: '',
        IssuingOrganization: '',
        IssueDate: '',
        ExpiryDate: '',
        CredentialId: '',
        CredentialUrl: ''
      }
    ]);
  };

  const updateCertification = (index, field, value) => {
    const updated = [...certifications];
    if (field === 'CertificationName') {
      updated[index] = { ...updated[index], [field]: formatName(value) };
    } else {
      updated[index] = { ...updated[index], [field]: value };
    }
    onChange(updated);
  };

  const removeCertification = (index) => {
    const updated = certifications.filter((_, i) => i !== index);
    onChange(updated);
  };

  return (
    <div>
      {certifications.map((cert, index) => (
        <div key={cert.CertificationId || index} style={{
          background: '#f0fdf4',
          borderRadius: '8px',
          padding: '16px',
          marginBottom: '16px',
          border: '1px solid #bbf7d0',
          position: 'relative'
        }}>
          <button
            onClick={() => removeCertification(index)}
            style={{
              position: 'absolute',
              top: '8px',
              right: '8px',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: '#ef4444'
            }}
          >
            <Trash2 size={16} />
          </button>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
            <input
              type="text"
              value={cert.CertificationName}
              onChange={(e) => updateCertification(index, 'CertificationName', e.target.value)}
              placeholder="Certification Name"
              style={{ padding: '8px', borderRadius: '4px', border: '1px solid #d1d5db' }}
            />
            <input
              type="text"
              value={cert.IssuingOrganization}
              onChange={(e) => updateCertification(index, 'IssuingOrganization', e.target.value)}
              placeholder="Issuing Organization"
              style={{ padding: '8px', borderRadius: '4px', border: '1px solid #d1d5db' }}
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
            <input
              type="month"
              value={cert.IssueDate}
              onChange={(e) => updateCertification(index, 'IssueDate', e.target.value)}
              placeholder="Issue Date"
              style={{ padding: '8px', borderRadius: '4px', border: '1px solid #d1d5db' }}
            />
            <input
              type="month"
              value={cert.ExpiryDate}
              onChange={(e) => updateCertification(index, 'ExpiryDate', e.target.value)}
              placeholder="Expiry Date"
              style={{ padding: '8px', borderRadius: '4px', border: '1px solid #d1d5db' }}
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <input
              type="text"
              value={cert.CredentialId}
              onChange={(e) => updateCertification(index, 'CredentialId', e.target.value)}
              placeholder="Credential ID"
              style={{ padding: '8px', borderRadius: '4px', border: '1px solid #d1d5db' }}
            />
            <input
              type="url"
              value={cert.CredentialUrl}
              onChange={(e) => updateCertification(index, 'CredentialUrl', e.target.value)}
              placeholder="Credential URL"
              style={{ padding: '8px', borderRadius: '4px', border: '1px solid #d1d5db' }}
            />
          </div>
        </div>
      ))}

      <button
        onClick={addCertification}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          padding: '8px 16px',
          background: '#f3f4f6',
          border: '1px dashed #3b5bfd',
          borderRadius: '4px',
          cursor: 'pointer',
          color: '#3b5bfd'
        }}
      >
        <Plus size={16} /> Add Certification
      </button>
    </div>
  );
};

// Documents Editor Component
const DocumentEditor = ({ documents, onChange, candidateId }) => {
  const [uploading, setUploading] = useState(false);

  const addDocument = () => {
    onChange([
      ...documents,
      {
        DocumentId: `temp-${Date.now()}-${documents.length}`,
        DocumentName: '',
        DocumentType: 'Certificate',
        File: null,
        FileName: '',
        isNew: true
      }
    ]);
  };

  const updateDocument = (index, field, value) => {
    const updated = [...documents];
    updated[index] = { ...updated[index], [field]: value };
    onChange(updated);
  };

  const removeDocument = (index) => {
    const updated = documents.filter((_, i) => i !== index);
    onChange(updated);
  };

  const handleFileUpload = (index, file) => {
    if (file) {
      const validTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'image/jpeg', 'image/png'];
      if (!validTypes.includes(file.type)) {
        Swal.fire('Error', 'Invalid file type. Only PDF, Word, and images are allowed.', 'error');
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        Swal.fire('Error', 'File size must be under 10MB.', 'error');
        return;
      }

      updateDocument(index, 'File', file);
      updateDocument(index, 'FileName', file.name);
      if (!documents[index].DocumentName) {
        updateDocument(index, 'DocumentName', file.name.replace(/\.[^/.]+$/, ""));
      }
    }
  };

  return (
    <div>
      {documents.map((doc, index) => (
        <div key={doc.DocumentId || index} style={{
          background: '#ffffff',
          borderRadius: '8px',
          padding: '16px',
          marginBottom: '16px',
          border: '1px solid #e5e7eb',
          position: 'relative'
        }}>
          <button
            onClick={() => removeDocument(index)}
            style={{
              position: 'absolute',
              top: '8px',
              right: '8px',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: '#ef4444'
            }}
          >
            <Trash2 size={16} />
          </button>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
            <input
              type="text"
              value={doc.DocumentName}
              onChange={(e) => updateDocument(index, 'DocumentName', e.target.value)}
              placeholder="Document Name"
              style={{ padding: '8px', borderRadius: '4px', border: '1px solid #d1d5db' }}
            />
            <select
              value={doc.DocumentType}
              onChange={(e) => updateDocument(index, 'DocumentType', e.target.value)}
              style={{ padding: '8px', borderRadius: '4px', border: '1px solid #d1d5db' }}
            >
              <option value="Certificate">Certificate</option>
              <option value="ID Proof">ID Proof</option>
              <option value="Offer Letter">Offer Letter</option>
              <option value="Degree">Degree</option>
              <option value="Transcript">Transcript</option>
              <option value="Other">Other</option>
            </select>
          </div>

          <div>
            <input
              type="file"
              id={`file-${index}`}
              onChange={(e) => handleFileUpload(index, e.target.files[0])}
              style={{ display: 'none' }}
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
            />
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <button
                onClick={() => document.getElementById(`file-${index}`).click()}
                style={{
                  padding: '6px 12px',
                  background: '#f3f4f6',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Choose File
              </button>
              <span style={{ fontSize: '13px', color: '#6b7280' }}>
                {doc.FileName || (doc.DocumentId ? 'Existing file' : 'No file selected')}
              </span>
            </div>
          </div>
        </div>
      ))}

      <button
        onClick={addDocument}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          padding: '8px 16px',
          background: '#f3f4f6',
          border: '1px dashed #3b5bfd',
          borderRadius: '4px',
          cursor: 'pointer',
          color: '#3b5bfd'
        }}
      >
        <Plus size={16} /> Add Document
      </button>
    </div>
  );
};

const CandidateDetail = ({ candidateId: propCandidateId, isModal = false, onClose, initialTab }) => {
  const { id: paramId } = useParams();
  const id = propCandidateId || paramId;
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(initialTab || 'overview');
  const [candidate, setCandidate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedCandidate, setEditedCandidate] = useState(null);
  const [saving, setSaving] = useState(false);
  const [selectedResumeId, setSelectedResumeId] = useState(null);
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const DEFAULT_BENCH_RECRUITERS = [
    { UserId: '10', UserName: 'Abinesh Anbazhagan' },
    { UserId: '1424', UserName: 'Karunakaran Palani' },
    { UserId: '1423', UserName: 'Pragadeeshwaran karthikeyan' },
    { UserId: '1425', UserName: 'Rohinth Chelladurai' }
  ];
  const [recruitersList, setRecruitersList] = useState(DEFAULT_BENCH_RECRUITERS);

  useEffect(() => {
    const fetchRecruiters = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${BASE_URL}/api/bench-manage/recruiters`, {
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
        });
        if (res.ok) {
          const data = await res.json();
          const rawRecruiters = data.recruiters || data.data || [];
          if (Array.isArray(rawRecruiters) && rawRecruiters.length > 0) {
            const mapped = rawRecruiters.map(r => ({
              UserId: String(r.id || r.UserId),
              UserName: r.name || r.UserName
            })).filter(r => r.UserName);
            setRecruitersList(mapped);
          }
        }
      } catch (err) {
        console.error('Failed to fetch recruiters:', err);
      }
    };

    const fetchCrmCompanies = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${BASE_URL}/api/crm/companies`, {
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
        });
        if (res.ok) {
          const data = await res.json();
          if (data && data.success && Array.isArray(data.data)) {
            setCrmCompanies(data.data);
          } else if (Array.isArray(data)) {
            setCrmCompanies(data);
          }
        }
      } catch (err) {
        console.error('Failed to fetch CRM companies:', err);
      }
    };

    fetchRecruiters();
    fetchCrmCompanies();
  }, []);

  // CRM Companies & Petitioner Dropdown state
  const [crmCompanies, setCrmCompanies] = useState([]);
  const [petitionerDropdownOpen, setPetitionerDropdownOpen] = useState(false);

  // Notes states
  const [notes, setNotes] = useState([]);
  const [notesLoading, setNotesLoading] = useState(false);
  const [newNoteText, setNewNoteText] = useState('');
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editNoteText, setEditNoteText] = useState('');
  const [isAddingNote, setIsAddingNote] = useState(false);
  const [savingNote, setSavingNote] = useState(false);
  const [newNoteCategory, setNewNoteCategory] = useState('GENERAL');
  const [editNoteCategory, setEditNoteCategory] = useState('GENERAL');

  // Submissions states & Date Filters
  const [submissions, setSubmissions] = useState([]);
  const [submissionsLoading, setSubmissionsLoading] = useState(false);
  const [dateFilters, setDateFilters] = useState({
    quickSelect: 'All Time',
    startDate: '',
    endDate: ''
  });
  const [appliedDateFilters, setAppliedDateFilters] = useState({
    startDate: '',
    endDate: ''
  });

  const toYMD = (d) => {
    if (!d || isNaN(new Date(d).getTime())) return '';
    const date = new Date(d);
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  const fetchSubmissions = useCallback(async (start = '', end = '') => {
    if (!id) return;
    setSubmissionsLoading(true);
    try {
      const token = localStorage.getItem('token');
      let url = `${BASE_URL}/api/resumes/${id}/submissions`;
      const params = [];
      if (start) params.push(`startDate=${start}`);
      if (end) params.push(`endDate=${end}`);
      if (params.length > 0) url += `?${params.join('&')}`;

      const res = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (res.ok) {
        const data = await res.json();
        if (data.success) {
          setSubmissions(data.data || []);
        }
      }
    } catch (err) {
      console.error('Failed to fetch candidate submissions:', err);
    } finally {
      setSubmissionsLoading(false);
    }
  }, [id]);

  const handleQuickSelectChange = (e) => {
    const val = e.target.value;
    const today = new Date();
    let start = null;
    let end = null;

    if (val === 'Today') {
      start = new Date(today);
      end = new Date(today);
    } else if (val === 'Yesterday') {
      start = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 1);
      end = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 1);
    } else if (val === 'This Week') {
      const day = today.getDay();
      const diff = today.getDate() - day + (day === 0 ? -6 : 1);
      start = new Date(today.getFullYear(), today.getMonth(), diff);
      end = new Date(today);
    } else if (val === 'Last Week') {
      const day = today.getDay();
      const diff = today.getDate() - day + (day === 0 ? -6 : 1) - 7;
      start = new Date(today.getFullYear(), today.getMonth(), diff);
      end = new Date(today.getFullYear(), today.getMonth(), diff + 6);
    } else if (val === 'This Month') {
      start = new Date(today.getFullYear(), today.getMonth(), 1);
      end = new Date(today);
    } else if (val === 'Last Month') {
      start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      end = new Date(today.getFullYear(), today.getMonth(), 0);
    } else if (val === 'This Quarter') {
      const quarter = Math.floor(today.getMonth() / 3);
      start = new Date(today.getFullYear(), quarter * 3, 1);
      end = new Date(today);
    } else if (val === 'Last Quarter') {
      const quarter = Math.floor(today.getMonth() / 3) - 1;
      start = new Date(today.getFullYear(), quarter * 3, 1);
      end = new Date(today.getFullYear(), (quarter + 1) * 3, 0);
    } else if (val === 'All Time') {
      start = null;
      end = null;
    }

    const sDate = start ? toYMD(start) : '';
    const eDate = end ? toYMD(end) : '';

    setDateFilters({
      quickSelect: val,
      startDate: sDate,
      endDate: eDate
    });

    setAppliedDateFilters({
      startDate: sDate,
      endDate: eDate
    });

    fetchSubmissions(sDate, eDate);
  };

  // Applications states & Date Filters (Careers Portal / ATS)
  const [applications, setApplications] = useState([]);
  const [applicationsLoading, setApplicationsLoading] = useState(false);
  const [appDateFilters, setAppDateFilters] = useState({
    quickSelect: 'All Time',
    startDate: '',
    endDate: ''
  });
  const [appliedAppDateFilters, setAppliedAppDateFilters] = useState({
    startDate: '',
    endDate: ''
  });
  const [appSearchTerm, setAppSearchTerm] = useState('');
  const [selectedAppScoreDetails, setSelectedAppScoreDetails] = useState(null);
  const [showAppScoreModal, setShowAppScoreModal] = useState(false);

  const fetchApplications = useCallback(async (start = '', end = '') => {
    if (!id) return;
    setApplicationsLoading(true);
    try {
      const token = localStorage.getItem('token');
      let url = `${BASE_URL}/api/resumes/${id}/applications`;
      const params = [];
      if (start) params.push(`startDate=${start}`);
      if (end) params.push(`endDate=${end}`);
      if (params.length > 0) url += `?${params.join('&')}`;

      const res = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (res.ok) {
        const data = await res.json();
        if (data.success) {
          setApplications(data.data || []);
        }
      }
    } catch (err) {
      console.error('Failed to fetch candidate applications:', err);
    } finally {
      setApplicationsLoading(false);
    }
  }, [id]);

  const handleAppQuickSelectChange = (e) => {
    const val = e.target.value;
    const today = new Date();
    let start = null;
    let end = null;

    if (val === 'Today') {
      start = new Date(today);
      end = new Date(today);
    } else if (val === 'Yesterday') {
      start = new Date(today);
      start.setDate(today.getDate() - 1);
      end = new Date(start);
    } else if (val === 'This Week') {
      const day = today.getDay();
      start = new Date(today);
      start.setDate(today.getDate() - day);
      end = new Date(today);
    } else if (val === 'Last Week') {
      const day = today.getDay();
      end = new Date(today);
      end.setDate(today.getDate() - day - 1);
      start = new Date(end);
      start.setDate(end.getDate() - 6);
    } else if (val === 'This Month') {
      start = new Date(today.getFullYear(), today.getMonth(), 1);
      end = new Date(today);
    } else if (val === 'Last Month') {
      start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      end = new Date(today.getFullYear(), today.getMonth(), 0);
    } else if (val === 'This Quarter') {
      const qMonth = Math.floor(today.getMonth() / 3) * 3;
      start = new Date(today.getFullYear(), qMonth, 1);
      end = new Date(today);
    } else if (val === 'Last Quarter') {
      const qMonth = Math.floor(today.getMonth() / 3) * 3;
      start = new Date(today.getFullYear(), qMonth - 3, 1);
      end = new Date(today.getFullYear(), qMonth, 0);
    } else if (val === 'All Time') {
      start = null;
      end = null;
    }

    const sDate = start ? toYMD(start) : '';
    const eDate = end ? toYMD(end) : '';

    setAppDateFilters({
      quickSelect: val,
      startDate: sDate,
      endDate: eDate
    });

    setAppliedAppDateFilters({
      startDate: sDate,
      endDate: eDate
    });

    fetchApplications(sDate, eDate);
  };

  const filteredApplications = useMemo(() => {
    if (!appSearchTerm.trim()) return applications;
    const term = appSearchTerm.toLowerCase();
    return applications.filter(app =>
      (app.JobTitle && app.JobTitle.toLowerCase().includes(term)) ||
      (app.position && app.position.toLowerCase().includes(term)) ||
      (app.ClientName && app.ClientName.toLowerCase().includes(term)) ||
      (app.JobCode && app.JobCode.toLowerCase().includes(term)) ||
      (app.employment_type && app.employment_type.toLowerCase().includes(term)) ||
      (app.visa_type && app.visa_type.toLowerCase().includes(term))
    );
  }, [applications, appSearchTerm]);

  // Location sub-fields for edit mode
  const [locCountry, setLocCountry] = useState('');
  const [locState, setLocState] = useState('');
  const [locCity, setLocCity] = useState('');
  const [stateSearch, setStateSearch] = useState('');

  // Dropdown Options
  const genderOptions = ['Male', 'Female', 'Other', 'Prefer not to say'];
  const maritalStatusOptions = ['Single', 'Married', 'Divorced', 'Widowed', 'Separated'];
  const statusOptions = ['Available', 'In Process', 'Hired', 'Not Available', 'On Hold'];
  const remoteOptions = ['Remote', 'OnSite', 'Hybrid'];
  const workAuthOptions = ['US Citizen', 'Green Card', 'H1-B Visa', 'TN Visa', 'EAD', 'F1-OPT', 'F1-CPT', 'Other'];
  const employmentTypeOptions = ['W2', 'C2C', '1099', 'Full-Time', 'Contract', 'Contract-to-Hire', 'Part-Time', 'Temporary', 'Internship', 'Freelance'];
  const securityClearanceOptions = ['None', 'Confidential', 'Secret', 'Top Secret', 'Top Secret/SCI', 'TS/SCI with Polygraph', 'Public Trust'];
  const industryOptions = ['Technology', 'Healthcare', 'Finance', 'Education', 'Manufacturing', 'Retail', 'Construction', 'Transportation', 'Hospitality', 'Legal', 'Marketing', 'Other'];
  const rateTypeOptions = ['Hourly', 'Daily', 'Weekly', 'Monthly', 'Annual'];
  const noticePeriodOptions = ['Immediate', '2 Weeks', '1 Month', 'Other'];
  const i140Options = ['YES', 'NO', 'IN_PROCESS', 'NA'];
  const c2cStructureOptions = ['OWN_CORP', 'THROUGH_VENDOR'];

  useEffect(() => {
    const fetchCandidate = async () => {
      setLoading(true);
      setError(null);

      try {
        const token = localStorage.getItem('token');
        const headers = {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        };

        const res = await fetch(`${BASE_URL}/api/resumes/${id}`, {
          headers: headers,
        });

        if (!res.ok) {
          if (res.status === 401) {
            throw new Error('Unauthorized - Please login again');
          } else if (res.status === 404) {
            throw new Error('Candidate not found');
          } else {
            throw new Error(`Server error: ${res.status}`);
          }
        }

        const raw = await res.json();
        const transformed = transformCandidate(raw);

        setCandidate(transformed);
        setEditedCandidate(transformed);
        if (!transformed.IsBench && activeTab === 'bench') {
          setActiveTab('overview');
        }

      } catch (err) {
        console.error('Error loading candidate detail:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    const fetchNotes = async () => {
      try {
        setNotesLoading(true);
        const token = localStorage.getItem('token');
        const res = await fetch(`${BASE_URL}/api/resumes/${id}/notes`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        if (res.ok) {
          const data = await res.json();
          if (data.success) {
            setNotes(data.notes || []);
          }
        }
      } catch (err) {
        console.error('Failed to fetch notes:', err);
      } finally {
        setNotesLoading(false);
      }
    };

    if (id) {
      fetchCandidate();
      fetchNotes();
      fetchSubmissions();
      fetchApplications();
    } else {
      setError('Invalid candidate ID');
      setLoading(false);
    }
  }, [id, fetchSubmissions, fetchApplications]);

  // Derived documents list (excluding resumes)
  const nonResumeDocs = useMemo(() => {
    const src = isEditing ? editedCandidate : candidate;
    return (src?.Documents || []).filter(doc =>
      doc.DocumentType?.toUpperCase() !== 'RESUME' &&
      !doc.IsPrimaryResume
    );
  }, [candidate, editedCandidate, isEditing]);

  // Filter unique resumes
  const resumes = useMemo(() => removeDuplicates(candidate?.Resumes || [], r => r.ResumeId), [candidate?.Resumes]);

  const handleDownload = async (documentId, fileName) => {
    if (!documentId) {
      alert('No document available for download');
      return;
    }

    try {
      setPreviewLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${BASE_URL}/api/resumes/${documentId}/download`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Download failed');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName || 'document';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download error:', error);
      alert('Failed to download document. Please try again.');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handlePreview = (documentId) => {
    if (!documentId) {
      alert('No document available for preview');
      return;
    }

    const token = localStorage.getItem('token');
    const previewUrl = `${BASE_URL}/api/resumes/${documentId}/preview?token=${token}`;
    window.open(previewUrl, '_blank');
  };

  const handleDeleteResume = async (documentId, fileName) => {
    if (!documentId) return;

    const { value: formValues } = await Swal.fire({
      title: 'Delete Resume Document?',
      html: `
        <div style="text-align: left; font-size: 14px; color: #374151;">
          <p style="margin-bottom: 12px;"><strong>File:</strong> ${fileName || 'Resume Document'}</p>
          <p style="margin-bottom: 8px; font-weight: 600;">Choose deletion option:</p>
          <div style="display: flex; flex-direction: column; gap: 10px; margin-top: 10px;">
            <label style="display: flex; align-items: flex-start; gap: 8px; cursor: pointer; padding: 10px; border: 1px solid #bfdbfe; border-radius: 6px; background: #eff6ff;">
              <input type="radio" name="deleteType" value="soft" checked style="margin-top: 3px;" />
              <div>
                <strong style="color: #1d4ed8;">Soft Delete (Archive / Hide from Profile)</strong>
                <div style="font-size: 12px; color: #4b5563; margin-top: 2px;">Hides this resume from the active profile. Retains file in database/storage for audit & compliance.</div>
              </div>
            </label>
            <label style="display: flex; align-items: flex-start; gap: 8px; cursor: pointer; padding: 10px; border: 1px solid #fecaca; border-radius: 6px; background: #fff5f5;">
              <input type="radio" name="deleteType" value="hard" style="margin-top: 3px;" />
              <div>
                <strong style="color: #dc2626;">Hard Delete (Permanent Removal)</strong>
                <div style="font-size: 12px; color: #6b7280; margin-top: 2px;">Permanently deletes the document record and removes the file from storage. <em>Cannot be undone!</em></div>
              </div>
            </label>
          </div>
        </div>
      `,
      focusConfirm: false,
      showCancelButton: true,
      confirmButtonText: 'Delete',
      confirmButtonColor: '#ef4444',
      cancelButtonText: 'Cancel',
      preConfirm: () => {
        const selected = document.querySelector('input[name="deleteType"]:checked');
        return selected ? selected.value : 'soft';
      }
    });

    if (!formValues) return;

    const deleteType = formValues;

    try {
      Swal.fire({
        title: deleteType === 'hard' ? 'Permanently deleting...' : 'Deleting...',
        text: 'Please wait',
        allowOutsideClick: false,
        didOpen: () => Swal.showLoading()
      });

      const token = localStorage.getItem('token');
      const response = await fetch(`${BASE_URL}/api/resumes/${id}/documents/${documentId}?type=${deleteType}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || 'Failed to delete resume');
      }

      // If the deleted resume was currently selected in preview, close it
      if (selectedResumeId === documentId) {
        setSelectedResumeId(null);
      }

      // Refresh candidate data to ensure new primary and remaining documents are loaded
      const refreshRes = await fetch(`${BASE_URL}/api/resumes/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (refreshRes.ok) {
        const raw = await refreshRes.json();
        const transformed = transformCandidate(raw);
        setCandidate(transformed);
        setEditedCandidate(transformed);
      } else {
        // Fallback: update local candidate.Documents
        setCandidate(prev => ({
          ...prev,
          Documents: (prev.Documents || []).filter(d => d.DocumentId !== documentId)
        }));
      }

      Swal.fire({
        title: 'Deleted!',
        text: data.message || `Resume ${deleteType === 'hard' ? 'permanently' : 'soft'} deleted successfully.`,
        icon: 'success',
        timer: 1600,
        showConfirmButton: false
      });

    } catch (err) {
      console.error('Delete error:', err);
      Swal.fire('Error', err.message || 'Failed to delete resume document.', 'error');
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
    const edited = { ...candidate };
    edited.Documents = (candidate.Documents || []).filter(doc => doc.DocumentType !== 'Resume');
    setEditedCandidate(edited);

    // Use structured location fields from DB (preferred over string parsing)
    const rawCity = candidate.LocationCity || '';
    const rawStateCode = candidate.LocationStateCode || '';
    const rawCountryIso2 = candidate.LocationCountryIso2 || '';

    console.log('Location from DB fields:', { rawCity, rawStateCode, rawCountryIso2 });

    if (rawCity || rawCountryIso2) {
      // We have structured data from DB
      setLocCity(rawCity);
      setLocCountry(rawCountryIso2);

      // Convert state Code → full Name for the state search input
      if (rawStateCode && rawCountryIso2) {
        const stateName = Object.entries(stateMapping[rawCountryIso2] || {})
          .find(([, code]) => code.toUpperCase() === rawStateCode.toUpperCase())?.[0];
        setLocState(stateName || rawStateCode);
      } else {
        setLocState(rawStateCode);
      }
    } else {
      // Fallback: parse the CurrentLocation string  (e.g. "Jersey City, NJ, US")
      const loc = (candidate.CurrentLocation || '').trim();
      console.log('Fallback: parsing CurrentLocation string:', loc);
      if (loc) {
        const parts = loc.split(',').map(s => s.trim()).filter(Boolean);
        setLocCity(parts[0] || '');
        if (parts.length >= 3) {
          const countryCode = parts[2];
          setLocCountry(countryCode);
          const stateValue = parts[1];
          if (stateValue.length <= 3) {
            const stateName = Object.entries(stateMapping[countryCode] || {})
              .find(([, code]) => code.toUpperCase() === stateValue.toUpperCase())?.[0];
            setLocState(stateName || stateValue);
          } else {
            setLocState(stateValue);
          }
        } else if (parts.length === 2) {
          setLocState(parts[1]);
        }
      } else {
        setLocCity(''); setLocState(''); setLocCountry('');
      }
    }
    setStateSearch('');
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditedCandidate({ ...candidate });
    setLocCity(''); setLocState(''); setLocCountry(''); setStateSearch('');
  };

  const handleFieldEdit = (fieldPath, value) => {
    const fields = fieldPath.split('.');
    setEditedCandidate(prev => {
      const newState = { ...prev };
      let current = newState;

      for (let i = 0; i < fields.length - 1; i++) {
        if (!current[fields[i]]) current[fields[i]] = {};
        current = current[fields[i]];
      }

      current[fields[fields.length - 1]] = value;
      return newState;
    });
  };

  const handleSave = async () => {
    try {
      setSaving(true);

      const formData = new FormData();

      // Basic fields
      formData.append('FirstName', formatName(editedCandidate.FirstName || ''));
      formData.append('LastName', formatName(editedCandidate.LastName || ''));
      formData.append('MiddleName', formatName(editedCandidate.MiddleName || ''));
      formData.append('EmailID', editedCandidate.Contact?.Email || '');
      formData.append('Phone', editedCandidate.Contact?.Phone || '');
      formData.append('Mobile', editedCandidate.Mobile || '');
      formData.append('JobTitle', formatJobTitle(editedCandidate.JobTitle || ''));
      formData.append('YearsOfExperience', editedCandidate.YearsOfExperience || '');
      formData.append('Gender', editedCandidate.Gender || '');
      formData.append('ProfileSummary', editedCandidate.ProfileSummary || '');
      formData.append('LinkedInProfile', editedCandidate.LinkedInUrl || '');
      formData.append('GitHubUrl', editedCandidate.GitHubUrl || '');
      formData.append('TwitterUrl', editedCandidate.TwitterUrl || '');
      formData.append('VideoResumeUrl', editedCandidate.VideoResumeUrl || '');
      formData.append('CandidateStatus', editedCandidate.CandidateStatus || 'Available');
      formData.append('RemoteStatus', editedCandidate.RemoteStatus || 'Remote');

      // Build CurrentLocation from sub-fields
      const builtLocation = (() => {
        const parts = [];

        // Add city if exists
        if (locCity?.trim()) {
          parts.push(locCity.trim());
        }

        // Add state (convert to code if needed)
        if (locState?.trim() && locCountry?.trim()) {
          const stateCode = getStateCode(locCountry, locState);
          parts.push(stateCode);
        } else if (locState?.trim()) {
          parts.push(locState.trim());
        }

        // Add country code
        if (locCountry?.trim()) {
          parts.push(locCountry.trim());
        }

        console.log('Built location parts:', parts); // Debug log

        // Return if we have any location parts
        if (parts.length >= 1) {
          return parts.join(', ');
        }

        // Fallback to existing location
        return editedCandidate.CurrentLocation || '';
      })();

      console.log('Saving location:', builtLocation); // Debug log
      formData.append('CurrentLocation', builtLocation);

      // New fields
      formData.append('WorkAuthorization', editedCandidate.WorkAuthorization || '');
      formData.append('EmploymentType', editedCandidate.EmploymentType || '');
      formData.append('SecurityClearance', editedCandidate.SecurityClearance || '');
      formData.append('WillingToRelocate', editedCandidate.WillingToRelocate ? 'true' : 'false');
      formData.append('IsBench', editedCandidate.IsBench ? 'true' : 'false');
      formData.append('ExpectedRateFrom', editedCandidate.ExpectedRateFrom || '');
      formData.append('ExpectedRateTo', editedCandidate.ExpectedRateTo || '');
      formData.append('ExpectedRateType', editedCandidate.ExpectedRateType || 'Hourly');
      formData.append('CurrentRate', editedCandidate.CurrentRate || '');
      formData.append('CurrentRateType', editedCandidate.CurrentRateType || 'Hourly');
      formData.append('MaritalStatus', editedCandidate.MaritalStatus || '');
      formData.append('Industry', editedCandidate.Industry || '');
      formData.append('CurrentEmployer', editedCandidate.CurrentEmployer || '');
      formData.append('CurrentSalary', editedCandidate.CurrentSalary || '');
      formData.append('ExpectedSalary', editedCandidate.ExpectedSalary || '');
      formData.append('NoticePeriod', editedCandidate.NoticePeriod || '');

      // Bench Details
      formData.append('BenchPriority', editedCandidate.BenchPriority || '');
      formData.append('OutOfJobSince', editedCandidate.OutOfJobSince || '');
      formData.append('NewJobDeadline', editedCandidate.NewJobDeadline || '');
      formData.append('RecentPlacementDate', editedCandidate.RecentPlacementDate || '');
      formData.append('WorkAuthPetitioner', editedCandidate.WorkAuthPetitioner || '');
      formData.append('WorkAuthExpiryDate', editedCandidate.WorkAuthExpiryDate || '');
      formData.append('BenchAssigneeUserName', editedCandidate.BenchAssignee || '');
      formData.append('BenchAssigneeUserId', editedCandidate.BenchAssigneeUserId || '');
      if (Array.isArray(editedCandidate.BenchAssignees)) {
        formData.append('BenchAssignees', JSON.stringify(editedCandidate.BenchAssignees));
      }
      formData.append('CurrentClientName', editedCandidate.CurrentClientName || '');
      formData.append('ReasonForLeaving', editedCandidate.ReasonForLeaving || '');
      formData.append('ReferenceText', editedCandidate.ReferenceText || '');
      formData.append('Remarks', editedCandidate.Remarks || '');
      formData.append('H1BTransferWilling', editedCandidate.H1BTransferWilling ? 'true' : 'false');
      formData.append('I140Status', editedCandidate.I140Status || '');
      formData.append('I140PriorityDate', editedCandidate.I140PriorityDate || '');
      formData.append('C2CCorpName', editedCandidate.C2CCorpName || '');
      formData.append('C2CFEIN', editedCandidate.C2CFEIN || '');
      formData.append('C2CStructure', editedCandidate.C2CStructure || '');
      formData.append('WillingToTravel', editedCandidate.WillingToTravel ? 'true' : 'false');
      formData.append('AvailableFromDate', editedCandidate.AvailableFromDate || '');
      formData.append('RelocationNotes', editedCandidate.RelocationNotes || '');
      formData.append('IsCurrentlyWorking', editedCandidate.IsCurrentlyWorking ? 'true' : 'false');

      // Skills
      const skillsList = (editedCandidate.Skills || []).map(s => s.SkillName).filter(Boolean);
      formData.append('Skills', JSON.stringify(skillsList));

      // Work Experience
      const workList = (editedCandidate.WorkExperience || [])
        .filter(exp => exp.Company || exp.JobTitle)
        .map(exp => ({
          Company: exp.Company,
          JobTitle: formatJobTitle(exp.JobTitle),
          StartDate: exp.StartDate,
          EndDate: exp.EndDate,
          IsCurrent: exp.IsCurrent,
          Description: exp.Description,
          WorkExperienceId: exp.WorkExperienceId && !exp.WorkExperienceId.toString().startsWith('temp-') ? exp.WorkExperienceId : null
        }));
      formData.append('WorkExperience', JSON.stringify(workList));

      // Education
      const eduList = (editedCandidate.Education || [])
        .filter(edu => edu.Institution || edu.Degree)
        .map(edu => ({
          Institution: formatName(edu.Institution),
          Degree: formatName(edu.Degree),
          FieldOfStudy: edu.FieldOfStudy,
          StartDate: edu.StartDate,
          EndDate: edu.EndDate,
          GPA: edu.GPA,
          EducationId: edu.EducationId && !edu.EducationId.toString().startsWith('temp-') ? edu.EducationId : null
        }));
      formData.append('Education', JSON.stringify(eduList));

      // Certifications
      const certList = (editedCandidate.Certifications || [])
        .filter(cert => cert.CertificationName)
        .map(cert => ({
          CertificationName: formatName(cert.CertificationName),
          IssuingOrganization: cert.IssuingOrganization,
          IssueDate: cert.IssueDate,
          ExpiryDate: cert.ExpiryDate,
          CredentialId: cert.CredentialId,
          CredentialUrl: cert.CredentialUrl,
          CertificationId: cert.CertificationId && !cert.CertificationId.toString().startsWith('temp-') ? cert.CertificationId : null
        }));
      formData.append('Certifications', JSON.stringify(certList));

      // Handle new document files
      const newDocs = (editedCandidate.Documents || []).filter(d => d.isNew && d.File);
      newDocs.forEach((doc, index) => {
        formData.append(`AdditionalDoc_${index}`, doc.File);
        formData.append(`AdditionalDoc_${index}_Name`, doc.DocumentName || doc.File.name);
        formData.append(`AdditionalDoc_${index}_Type`, doc.DocumentType || 'Certificate');
      });
      formData.append('AdditionalDocsCount', String(newDocs.length));

      const response = await fetch(`${BASE_URL}/api/resumes/${id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: formData,
      });

      const responseData = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(responseData.error || responseData.detail || `HTTP error ${response.status}`);
      }

      // Refresh candidate data
      const refreshRes = await fetch(`${BASE_URL}/api/resumes/${id}`, {
        headers: getAuthHeaders(),
      });
      const refreshedRaw = await refreshRes.json();
      const refreshedCandidate = transformCandidate(refreshedRaw);

      setCandidate(refreshedCandidate);
      setEditedCandidate(refreshedCandidate);
      setIsEditing(false);

      // Notify parent lists (ResumeDashboard / BenchDashboard) to update immediately
      window.dispatchEvent(new CustomEvent('candidateListUpdated', { detail: { candidateId: id } }));

      Swal.fire({
        title: 'Success!',
        text: 'Candidate updated successfully.',
        icon: 'success',
        timer: 1800,
        showConfirmButton: false
      });

    } catch (error) {
      console.error('Error updating candidate:', error);
      Swal.fire({
        title: 'Error',
        text: error.message || 'Failed to update candidate',
        icon: 'error'
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    if (isModal) {
      return (
        <div
          className="onb-candidate-modal-overlay"
          onClick={(e) => { if (e.target === e.currentTarget && onClose) onClose(); }}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(15, 23, 42, 0.65)',
            backdropFilter: 'blur(4px)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '24px'
          }}
        >
          <div
            className="onb-candidate-modal-dialog"
            style={{
              backgroundColor: '#fff',
              borderRadius: '12px',
              padding: '48px 32px',
              textAlign: 'center',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '16px',
              minWidth: '320px'
            }}
          >
            <div className="cm-loading-spinner"></div>
            <p className="cm-loading-text" style={{ margin: 0, fontWeight: 600, color: '#475569' }}>Loading candidate details...</p>
            <button type="button" onClick={onClose} style={{ marginTop: '8px', padding: '6px 16px', background: 'transparent', border: '1px solid #cbd5e1', borderRadius: '6px', cursor: 'pointer', fontSize: '0.85rem', color: '#64748b', fontWeight: 500 }}>Cancel</button>
          </div>
        </div>
      );
    }
    return (
      <div className="cm-loading-container">
        <div className="cm-loading-spinner"></div>
        <p className="cm-loading-text">Loading candidate details...</p>
      </div>
    );
  }

  if (error || !candidate) {
    if (isModal) {
      return (
        <div
          className="onb-candidate-modal-overlay"
          onClick={(e) => { if (e.target === e.currentTarget && onClose) onClose(); }}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(15, 23, 42, 0.65)',
            backdropFilter: 'blur(4px)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '24px'
          }}
        >
          <div
            className="onb-candidate-modal-dialog"
            style={{
              backgroundColor: '#fff',
              borderRadius: '12px',
              padding: '36px 32px',
              textAlign: 'center',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '12px',
              minWidth: '320px'
            }}
          >
            <p style={{ margin: 0, color: '#ef4444', fontWeight: 600, fontSize: '1rem' }}>{error || 'Candidate details not found.'}</p>
            <button type="button" onClick={onClose} style={{ marginTop: '8px', padding: '8px 22px', background: '#3b5bfd', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 600, fontSize: '0.88rem' }}>Close</button>
          </div>
        </div>
      );
    }
    return (
      <div className="candidate-detail-container">
        <div style={{
          padding: '40px 20px',
          color: '#ef4444',
          fontSize: 16,
          textAlign: 'center',
          backgroundColor: '#fef2f2',
          borderRadius: '8px',
          margin: '20px'
        }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>❌</div>
          <div style={{ fontWeight: 600, marginBottom: 8 }}>Error Loading Candidate</div>
          <div style={{ color: '#666', marginBottom: 20 }}>{error || 'Candidate not found.'}</div>
          <button
            onClick={() => window.history.length > 1 ? navigate(-1) : navigate('/resume-dashboard')}
            className="candidate-detail-btn"
            style={{ margin: '0 auto' }}
          >
            <ArrowLeft size={16} /> Back to Candidates
          </button>
        </div>
      </div>
    );
  }

  const c = isEditing ? editedCandidate : candidate;
  const fullName = formatFullName(c.FirstName, c.MiddleName, c.LastName);
  const statusStyle = STATUS_COLORS[c.CandidateStatus] || STATUS_COLORS['On Hold'];
  const remoteStyle = REMOTE_COLORS[c.RemoteStatus] || REMOTE_COLORS['OnSite'];
  const bgColor = avatarColor(fullName || 'Unknown');
  const initials = getInitials(c.FirstName, c.LastName);

  // Get currency symbol based on country
  const currencySymbol = CURRENCY_MAP[locCountry]?.symbol || '$';

  // Format expected rate range with currency symbol
  const expectedRateRange = c.ExpectedRateFrom && c.ExpectedRateTo
    ? `${currencySymbol}${c.ExpectedRateFrom} - ${currencySymbol}${c.ExpectedRateTo} ${c.ExpectedRateType}`
    : c.ExpectedRateFrom
      ? `${currencySymbol}${c.ExpectedRateFrom} ${c.ExpectedRateType}`
      : c.ExpectedRateTo
        ? `Up to ${currencySymbol}${c.ExpectedRateTo} ${c.ExpectedRateType}`
        : null;

  const currentRateValue = c.CurrentRate
    ? `${currencySymbol}${c.CurrentRate} ${c.CurrentRateType}`
    : null;

  const hasEmail = (c.Contact?.Email || '').trim();
  const hasPhone = (c.Contact?.Phone || '').trim() || (c.Contact?.Mobile || '').trim() || (c.Mobile || '').trim();

  let candidateCategory = 'Sourcing';
  let categoryStyles = { bg: '#eff6ff', color: '#1d4ed8', border: '#bfdbfe' };

  if (!hasEmail && !hasPhone) {
    candidateCategory = 'Need to Review';
    categoryStyles = { bg: '#fef3c7', color: '#d97706', border: '#fde68a' };
  } else if (c.IsBench) {
    candidateCategory = 'Bench';
    categoryStyles = { bg: '#e0f2fe', color: '#0369a1', border: '#bae6fd' };
  }


  const tabs = [
    'overview',
    ...(c.IsBench ? ['bench'] : []),
    'experience',
    'education',
    'resume',
    'documents',
    'notes',
    'submissions',
    'applications'
  ];


  const handleAddNote = async () => {
    if (!newNoteText.trim()) return;
    setSavingNote(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${BASE_URL}/api/resumes/${id}/notes`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ noteText: newNoteText, category: newNoteCategory })
      });
      const data = await res.json();
      if (data.success) {
        setNewNoteText('');
        setNewNoteCategory('GENERAL');
        setIsAddingNote(false);
        // Refresh notes to get updated list with names/times
        const refreshRes = await fetch(`${BASE_URL}/api/resumes/${id}/notes`, { headers: { 'Authorization': `Bearer ${token}` } });
        if (refreshRes.ok) {
          const refreshData = await refreshRes.json();
          if (refreshData.success) setNotes(refreshData.notes);
        }
      } else {
        Swal.fire('Error', data.error || 'Failed to add note', 'error');
      }
    } catch (err) {
      console.error(err);
      Swal.fire('Error', 'Failed to add note', 'error');
    } finally {
      setSavingNote(false);
    }
  };

  const handleEditNoteSave = async (noteId) => {
    if (!editNoteText.trim()) return;
    setSavingNote(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${BASE_URL}/api/resumes/notes/${noteId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ noteText: editNoteText, category: editNoteCategory })
      });
      const data = await res.json();
      if (data.success) {
        setEditingNoteId(null);
        setEditNoteText('');
        // Refresh notes
        const refreshRes = await fetch(`${BASE_URL}/api/resumes/${id}/notes`, { headers: { 'Authorization': `Bearer ${token}` } });
        if (refreshRes.ok) {
          const refreshData = await refreshRes.json();
          if (refreshData.success) setNotes(refreshData.notes);
        }
      } else {
        Swal.fire('Error', data.error || 'Failed to update note', 'error');
      }
    } catch (err) {
      console.error(err);
      Swal.fire('Error', 'Failed to update note', 'error');
    } finally {
      setSavingNote(false);
    }
  };

  const handleDeleteNote = async (noteId) => {
    const confirm = await Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!'
    });

    if (confirm.isConfirmed) {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${BASE_URL}/api/resumes/notes/${noteId}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await res.json();
        if (data.success) {
          const refreshRes = await fetch(`${BASE_URL}/api/resumes/${id}/notes`, { headers: { 'Authorization': `Bearer ${token}` } });
          if (refreshRes.ok) {
            const refreshData = await refreshRes.json();
            if (refreshData.success) setNotes(refreshData.notes);
          }
          Swal.fire('Deleted!', 'Your note has been deleted.', 'success');
        } else {
          Swal.fire('Error', data.error || 'Failed to delete note', 'error');
        }
      } catch (err) {
        console.error(err);
        Swal.fire('Error', 'Failed to delete note', 'error');
      }
    }
  };

  const content = (
    <div className="candidate-detail-container">

      {/* Breadcrumb */}
      {!isModal && (
        <div className="cm-breadcrumb">
          <span className="cm-breadcrumb-home" onClick={() => navigate('/')}>Home</span>
          <ChevronRight size={13} className="cm-breadcrumb-sep" />
          <span className="cm-breadcrumb-home" onClick={() => navigate(-1)}>Candidates</span>
          <ChevronRight size={13} className="cm-breadcrumb-sep" />
          <span className="cm-breadcrumb-current">{fullName}</span>
        </div>
      )}

      {/* Header with Edit/Save buttons */}
      <div className="candidate-detail-header">
        <div className="candidate-detail-title" style={{ display: 'flex', alignItems: 'flex-start', gap: 20 }}>
          <div style={{
            width: 72, height: 72, borderRadius: '50%',
            background: bgColor, color: '#fff',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 26, fontWeight: 800, flexShrink: 0,
            boxShadow: '0 4px 14px rgba(0,0,0,0.15)',
          }}>
            {initials}
          </div>
          <div style={{ flex: 1 }}>
            {isEditing ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                <input
                  type="text"
                  value={c.FirstName || ''}
                  onChange={(e) => handleFieldEdit('FirstName', e.target.value)}
                  onBlur={(e) => handleFieldEdit('FirstName', formatName(e.target.value))}
                  placeholder="First Name"
                  style={{ padding: '8px', borderRadius: '4px', border: '1px solid #3b5bfd', fontSize: '20px', fontWeight: 'bold', flex: 1 }}
                />
                <input
                  type="text"
                  value={c.MiddleName || ''}
                  onChange={(e) => handleFieldEdit('MiddleName', e.target.value)}
                  onBlur={(e) => handleFieldEdit('MiddleName', formatName(e.target.value))}
                  placeholder="Middle Name"
                  style={{ padding: '8px', borderRadius: '4px', border: '1px solid #3b5bfd', fontSize: '20px', fontWeight: 'bold', flex: 1 }}
                />
                <input
                  type="text"
                  value={c.LastName || ''}
                  onChange={(e) => handleFieldEdit('LastName', e.target.value)}
                  onBlur={(e) => handleFieldEdit('LastName', formatName(e.target.value))}
                  placeholder="Last Name"
                  style={{ padding: '8px', borderRadius: '4px', border: '1px solid #3b5bfd', fontSize: '20px', fontWeight: 'bold', flex: 1 }}
                />
              </div>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <h1 style={{ margin: 0 }}>{fullName}</h1>
                <span style={{
                  padding: '4px 10px',
                  borderRadius: '12px',
                  fontSize: '12px',
                  fontWeight: 600,
                  backgroundColor: categoryStyles.bg,
                  color: categoryStyles.color,
                  border: `1px solid ${categoryStyles.border}`
                }}>
                  {candidateCategory}
                </span>
              </div>
            )}
            {isEditing ? (
              <input
                type="text"
                value={c.JobTitle || ''}
                onChange={(e) => handleFieldEdit('JobTitle', e.target.value)}
                onBlur={(e) => handleFieldEdit('JobTitle', formatJobTitle(e.target.value))}
                placeholder="Job Title"
                style={{ marginTop: '8px', padding: '8px', borderRadius: '4px', border: '1px solid #3b5bfd', fontSize: '18px', width: '100%' }}
              />
            ) : (
              <h2 style={{ margin: '4px 0 0', color: '#666', fontSize: 18 }}>{c.JobTitle || 'No Title Specified'}</h2>
            )}
          </div>
        </div>

        <div className="candidate-detail-actions" style={{ display: 'flex', gap: '10px' }}>
          {isEditing ? (
            <>
              <button
                className="candidate-detail-btn candidate-detail-btn-outline"
                onClick={handleCancel}
                disabled={saving}
                style={{ borderColor: '#ef4444', color: '#ef4444' }}
              >
                <X size={16} /> Cancel
              </button>
              <button
                className="candidate-detail-btn candidate-detail-btn-primary"
                onClick={handleSave}
                disabled={saving}
                style={{ background: '#3b5bfd', color: '#fff' }}
              >
                <Save size={16} /> {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </>
          ) : (
            <>
              {!isModal && (
                <button className="candidate-detail-btn candidate-detail-btn-outline" onClick={() => window.history.length > 1 ? navigate(-1) : navigate('/resume-dashboard')}>
                  <ArrowLeft size={16} /> Back
                </button>
              )}
              <button
                className="candidate-detail-btn candidate-detail-btn-primary"
                onClick={() => setShowSubmitModal(true)}
                style={{ background: '#3b5bfd', color: '#fff' }}
              >
                <Send size={16} /> Submit Profile
              </button>
              <button
                className="candidate-detail-btn candidate-detail-btn-primary"
                onClick={handleEdit}
                style={{ background: '#3b5bfd', color: '#fff' }}
              >
                <Edit size={16} /> Edit
              </button>
            </>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="candidate-detail-tabs">
        {tabs.map(tab => (
          <button
            key={tab}
            className={`candidate-detail-tab ${activeTab === tab ? 'active' : ''}`}
            onClick={() => { setActiveTab(tab); setSelectedResumeId(null); }}
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
            {tab === 'notes' && notes.length > 0 && (
              <span style={{
                marginLeft: '6px',
                background: activeTab === 'notes' ? '#3b5bfd' : '#f3f4f6',
                color: activeTab === 'notes' ? '#fff' : '#4b5563',
                padding: '1px 6px',
                borderRadius: '10px',
                fontSize: '11px',
                fontWeight: 600
              }}>
                {notes.length}
              </span>
            )}
            {tab === 'submissions' && (
              <span style={{
                marginLeft: '6px',
                background: activeTab === 'submissions' ? '#3b5bfd' : '#e0f2fe',
                color: activeTab === 'submissions' ? '#fff' : '#0369a1',
                padding: '1px 6px',
                borderRadius: '10px',
                fontSize: '11px',
                fontWeight: 600
              }}>
                {submissions.length}
              </span>
            )}
            {tab === 'applications' && (
              <span style={{
                marginLeft: '6px',
                background: activeTab === 'applications' ? '#3b5bfd' : '#dcfce7',
                color: activeTab === 'applications' ? '#fff' : '#15803d',
                padding: '1px 6px',
                borderRadius: '10px',
                fontSize: '11px',
                fontWeight: 600
              }}>
                {applications.length}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="candidate-detail-content">

        {/* OVERVIEW */}
        {activeTab === 'overview' && (
          <div>
            <div className="candidate-info-grid">
              {/* Personal Information Card */}
              <EditSection isEditing={isEditing}>
                <div className="candidate-info-card">
                  <h3><User size={16} style={{ marginRight: 8 }} />Personal Information</h3>
                  <InfoRow
                    label="Full Name"
                    value={fullName}
                    isEditing={false}
                  />
                  <InfoRow
                    label="Gender"
                    value={c.Gender}
                    isEditing={isEditing}
                    fieldName="Gender"
                    onEdit={handleFieldEdit}
                    options={genderOptions}
                  />
                  <InfoRow
                    label="Marital Status"
                    value={c.MaritalStatus}
                    isEditing={isEditing}
                    fieldName="MaritalStatus"
                    onEdit={handleFieldEdit}
                    options={maritalStatusOptions}
                  />
                  <InfoRow label="Candidate Code" value={c.CandidateCode} isEditing={false} />
                  {c.EmployeeCode && (
                    <InfoRow label="Employee ID" value={c.EmployeeCode} isEditing={false} />
                  )}
                  <InfoRow
                    label="Years of Experience"
                    value={c.YearsOfExperience}
                    displayValue={c.YearsOfExperience != null ? `${c.YearsOfExperience} years` : null}
                    isEditing={isEditing}
                    fieldName="YearsOfExperience"
                    onEdit={handleFieldEdit}
                    type="number"
                  />
                  <InfoRow
                    label="Current Location"
                    value={c.CurrentLocation}
                    isEditing={false}
                    fieldName="CurrentLocation"
                    onEdit={handleFieldEdit}
                  />
                  {isEditing && (
                    <div style={{ marginBottom: '8px' }}>
                      {/* Country */}
                      <div className="candidate-info-row">
                        <span className="candidate-info-label">Country</span>
                        <span className="candidate-info-value">
                          <select
                            value={locCountry}
                            onChange={e => {
                              setLocCountry(e.target.value);
                              setLocState('');
                              setStateSearch('');
                            }}
                            className="edit-select"
                            style={{ padding: '4px 8px', borderRadius: '4px', border: '1px solid #3b5bfd', fontSize: '13px', width: '100%' }}
                          >
                            <option value="">Select Country</option>
                            {countriesData.map(c => (
                              <option key={c.code} value={c.code}>{c.name}</option>
                            ))}
                          </select>
                        </span>
                      </div>

                      {/* State */}
                      <div className="candidate-info-row">
                        <span className="candidate-info-label">State</span>
                        <span className="candidate-info-value">
                          {locCountry ? (
                            <div style={{ position: 'relative' }}>
                              <input
                                type="text"
                                placeholder="Search and select state..."
                                value={stateSearch !== '' ? stateSearch : locState}
                                onChange={e => {
                                  setStateSearch(e.target.value);
                                }}
                                onFocus={() => {
                                  setStateSearch(locState);
                                }}
                                onBlur={() => {
                                  setTimeout(() => setStateSearch(''), 150);
                                }}
                                className="edit-input"
                                autoComplete="off"
                                style={{ padding: '4px 8px', borderRadius: '4px', border: '1px solid #3b5bfd', fontSize: '13px', width: '100%' }}
                              />
                              {stateSearch !== '' && (
                                <div style={{
                                  position: 'absolute', top: '100%', left: 0, right: 0,
                                  background: '#fff', border: '1px solid #3b5bfd',
                                  borderRadius: '4px', maxHeight: '180px', overflowY: 'auto',
                                  zIndex: 9999, boxShadow: '0 4px 12px rgba(0,0,0,0.12)'
                                }}>
                                  {getStatesForCountry(locCountry)
                                    .filter(s => s.toLowerCase().includes(stateSearch.toLowerCase()))
                                    .slice(0, 20)
                                    .map(s => (
                                      <div
                                        key={s}
                                        onMouseDown={e => {
                                          e.preventDefault(); // Prevent onBlur from firing before click registers
                                          setLocState(s);
                                          setStateSearch('');
                                        }}
                                        style={{
                                          padding: '7px 12px', cursor: 'pointer',
                                          fontSize: '13px', borderBottom: '1px solid #f3f4f6',
                                          background: locState === s ? '#e6f7f5' : '#fff', color: '#374151'
                                        }}
                                        onMouseEnter={e => e.currentTarget.style.background = '#f0faf9'}
                                        onMouseLeave={e => e.currentTarget.style.background = locState === s ? '#e6f7f5' : '#fff'}
                                      >
                                        {s}
                                      </div>
                                    ))}
                                  {getStatesForCountry(locCountry).filter(s => s.toLowerCase().includes(stateSearch.toLowerCase())).length === 0 && (
                                    <div style={{ padding: '8px 12px', fontSize: '12px', color: '#9ca3af' }}>No states found</div>
                                  )}
                                </div>
                              )}
                            </div>
                          ) : (
                            <input
                              type="text"
                              className="edit-input"
                              placeholder="Select country first"
                              disabled
                              value=""
                              style={{ padding: '4px 8px', borderRadius: '4px', border: '1px solid #d1d5db', fontSize: '13px', width: '100%', background: '#f9fafb', color: '#9ca3af' }}
                            />
                          )}
                        </span>
                      </div>

                      {/* City */}
                      <div className="candidate-info-row">
                        <span className="candidate-info-label">City</span>
                        <span className="candidate-info-value">
                          <input
                            type="text"
                            value={locCity}
                            onChange={e => setLocCity(e.target.value)}
                            placeholder="Enter city name"
                            className="edit-input"
                            style={{ padding: '4px 8px', borderRadius: '4px', border: '1px solid #3b5bfd', fontSize: '13px', width: '100%' }}
                          />
                        </span>
                      </div>
                    </div>
                  )}
                  <InfoRow
                    label="Willing to Relocate"
                    value={c.WillingToRelocate}
                    displayValue={c.WillingToRelocate ?
                      <span style={{ color: '#10b981', display: 'flex', alignItems: 'center', gap: 4 }}>
                        <CheckCircle size={14} /> Yes
                      </span> :
                      <span style={{ color: '#ef4444', display: 'flex', alignItems: 'center', gap: 4 }}>
                        <XCircle size={14} /> No
                      </span>
                    }
                    isEditing={isEditing}
                    fieldName="WillingToRelocate"
                    onEdit={handleFieldEdit}
                    type="checkbox"
                  />
                  <InfoRow
                    label="Bench Candidate"
                    value={c.IsBench}
                    displayValue={c.IsBench ?
                      <span style={{ color: '#f59e0b', display: 'flex', alignItems: 'center', gap: 4, fontWeight: 600 }}>
                        <CheckCircle size={14} /> Yes — On Bench
                      </span> :
                      <span style={{ color: '#6b7280', display: 'flex', alignItems: 'center', gap: 4 }}>
                        <XCircle size={14} /> No
                      </span>
                    }
                    isEditing={isEditing}
                    fieldName="IsBench"
                    onEdit={handleFieldEdit}
                    type="checkbox"
                  />
                </div>
              </EditSection>

              {/* Contact Card - WITH COPY FUNCTIONALITY */}
              <EditSection isEditing={isEditing}>
                <div className="candidate-info-card">
                  <h3 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Mail size={16} color="#3b5bfd" />
                    Contact & Social
                  </h3>
                  <InfoRow
                    label="Email"
                    value={c.Contact?.Email}
                    displayValue={c.Contact?.Email ? (
                      <CopyableText
                        text={c.Contact.Email}
                        icon={Mail}
                        label="Email"
                      />
                    ) : null}
                    isEditing={isEditing}
                    fieldName="Contact.Email"
                    onEdit={handleFieldEdit}
                    type="email"
                  />

                  <InfoRow
                    label="Mobile"
                    value={c.Mobile || c.Contact?.Phone || c.Contact?.Mobile}
                    displayValue={(c.Mobile || c.Contact?.Phone || c.Contact?.Mobile) ? (
                      <CopyableText
                        text={formatPhone(c.Mobile || c.Contact?.Phone || c.Contact?.Mobile)}
                        icon={Phone}
                        label="Mobile number"
                      />
                    ) : null}
                    isEditing={isEditing}
                    fieldName="Mobile"
                    onEdit={handleFieldEdit}
                    type="mobile"
                    countryCode={locCountry}
                  />

                  <InfoRow
                    label="LinkedIn"
                    value={c.LinkedInUrl}
                    displayValue={c.LinkedInUrl ? (
                      <CopyableLink
                        href={c.LinkedInUrl}
                        text="View Profile"
                        icon={Linkedin}
                        label="LinkedIn"
                      />
                    ) : null}
                    isEditing={isEditing}
                    fieldName="LinkedInUrl"
                    onEdit={handleFieldEdit}
                    type="url"
                  />

                  <InfoRow
                    label="GitHub"
                    value={c.GitHubUrl}
                    displayValue={c.GitHubUrl ? (
                      <CopyableLink
                        href={c.GitHubUrl}
                        text="View Profile"
                        icon={Github}
                        label="GitHub"
                      />
                    ) : null}
                    isEditing={isEditing}
                    fieldName="GitHubUrl"
                    onEdit={handleFieldEdit}
                    type="url"
                  />

                  <InfoRow
                    label="Twitter"
                    value={c.TwitterUrl}
                    displayValue={c.TwitterUrl ? (
                      <CopyableLink
                        href={c.TwitterUrl}
                        text="View Profile"
                        icon={Twitter}
                        label="Twitter"
                      />
                    ) : null}
                    isEditing={isEditing}
                    fieldName="TwitterUrl"
                    onEdit={handleFieldEdit}
                    type="url"
                  />

                  <InfoRow
                    label="Video Resume"
                    value={c.VideoResumeUrl}
                    displayValue={c.VideoResumeUrl ? (
                      <CopyableLink
                        href={c.VideoResumeUrl}
                        text="Watch Video"
                        icon={Video}
                        label="Video Resume"
                      />
                    ) : null}
                    isEditing={isEditing}
                    fieldName="VideoResumeUrl"
                    onEdit={handleFieldEdit}
                    type="url"
                  />
                </div>
              </EditSection>

              {/* Employment Details Card */}
              <EditSection isEditing={isEditing}>
                <div className="candidate-info-card">
                  <h3><Briefcase size={16} style={{ marginRight: 8 }} />Employment Details</h3>
                  <InfoRow
                    label="Work Authorization"
                    value={c.WorkAuthorization}
                    isEditing={isEditing}
                    fieldName="WorkAuthorization"
                    onEdit={handleFieldEdit}
                    options={workAuthOptions}
                  />
                  <InfoRow
                    label="Employment Type"
                    value={c.EmploymentType}
                    isEditing={isEditing}
                    fieldName="EmploymentType"
                    onEdit={handleFieldEdit}
                    options={employmentTypeOptions}
                  />
                  <InfoRow
                    label="Security Clearance"
                    value={c.SecurityClearance}
                    isEditing={isEditing}
                    fieldName="SecurityClearance"
                    onEdit={handleFieldEdit}
                    options={securityClearanceOptions}
                  />
                  <InfoRow
                    label="Industry"
                    value={c.Industry}
                    isEditing={isEditing}
                    fieldName="Industry"
                    onEdit={handleFieldEdit}
                    options={industryOptions}
                  />
                  <InfoRow
                    label="Current Employer"
                    value={c.CurrentEmployer}
                    isEditing={isEditing}
                    fieldName="CurrentEmployer"
                    onEdit={handleFieldEdit}
                  />
                  <InfoRow
                    label="Current Salary"
                    value={c.CurrentSalary}
                    isEditing={isEditing}
                    fieldName="CurrentSalary"
                    onEdit={handleFieldEdit}
                  />
                  <InfoRow
                    label="Expected Salary"
                    value={c.ExpectedSalary}
                    isEditing={isEditing}
                    fieldName="ExpectedSalary"
                    onEdit={handleFieldEdit}
                  />
                  <InfoRow
                    label="Notice Period"
                    value={c.NoticePeriod}
                    isEditing={isEditing}
                    fieldName="NoticePeriod"
                    onEdit={handleFieldEdit}
                  />
                  <InfoRow
                    label="Remote Status"
                    value={c.RemoteStatus}
                    displayValue={
                      <span className="cm-status-badge" style={{ background: remoteStyle.bg, color: remoteStyle.color }}>
                        {c.RemoteStatus === 'OnSite' ? 'On-Site' : c.RemoteStatus}
                      </span>
                    }
                    isEditing={isEditing}
                    fieldName="RemoteStatus"
                    onEdit={handleFieldEdit}
                    options={remoteOptions}
                  />
                </div>
              </EditSection>

              {/* Compensation & Work Auth Card */}
              <EditSection isEditing={isEditing}>
                <div className="candidate-info-card">
                  <h3 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <DollarSign size={16} color="#3b5bfd" />
                    Compensation & Work Auth
                  </h3>

                  {/* Expected Rate */}
                  <div className="candidate-info-row">
                    <span className="candidate-info-label">Expected Rate</span>
                    <span className="candidate-info-value">
                      {isEditing ? (
                        <div style={{ display: 'flex', gap: '5px', width: '100%' }}>
                          <div style={{ position: 'relative', width: '30%' }}>
                            <span style={{ position: 'absolute', left: '8px', top: '50%', transform: 'translateY(-50%)', fontSize: '13px', color: '#6b7280' }}>
                              {currencySymbol}
                            </span>
                            <input
                              type="number"
                              value={c.ExpectedRateFrom || ''}
                              onChange={(e) => handleFieldEdit('ExpectedRateFrom', e.target.value)}
                              placeholder="From"
                              style={{ padding: '4px 4px 4px 24px', borderRadius: '4px', border: '1px solid #3b5bfd', width: '100%', fontSize: '13px' }}
                            />
                          </div>
                          <div style={{ position: 'relative', width: '30%' }}>
                            <span style={{ position: 'absolute', left: '8px', top: '50%', transform: 'translateY(-50%)', fontSize: '13px', color: '#6b7280' }}>
                              {currencySymbol}
                            </span>
                            <input
                              type="number"
                              value={c.ExpectedRateTo || ''}
                              onChange={(e) => handleFieldEdit('ExpectedRateTo', e.target.value)}
                              placeholder="To"
                              style={{ padding: '4px 4px 4px 24px', borderRadius: '4px', border: '1px solid #3b5bfd', width: '100%', fontSize: '13px' }}
                            />
                          </div>
                          <select
                            value={c.ExpectedRateType || 'Hourly'}
                            onChange={(e) => handleFieldEdit('ExpectedRateType', e.target.value)}
                            style={{ padding: '4px', borderRadius: '4px', border: '1px solid #3b5bfd', width: '40%', fontSize: '13px' }}
                          >
                            {rateTypeOptions.map(opt => (
                              <option key={opt} value={opt}>{opt}</option>
                            ))}
                          </select>
                        </div>
                      ) : (
                        expectedRateRange || <span style={{ color: '#9ca3af' }}>—</span>
                      )}
                    </span>
                  </div>

                  {/* Current Rate */}
                  <div className="candidate-info-row">
                    <span className="candidate-info-label">Current Rate</span>
                    <span className="candidate-info-value">
                      {isEditing ? (
                        <div style={{ display: 'flex', gap: '5px', width: '100%' }}>
                          <div style={{ position: 'relative', width: '60%' }}>
                            <span style={{ position: 'absolute', left: '8px', top: '50%', transform: 'translateY(-50%)', fontSize: '13px', color: '#6b7280' }}>
                              {currencySymbol}
                            </span>
                            <input
                              type="number"
                              value={c.CurrentRate || ''}
                              onChange={(e) => handleFieldEdit('CurrentRate', e.target.value)}
                              placeholder="Current Rate"
                              style={{ padding: '4px 4px 4px 24px', borderRadius: '4px', border: '1px solid #3b5bfd', width: '100%', fontSize: '13px' }}
                            />
                          </div>
                          <select
                            value={c.CurrentRateType || 'Hourly'}
                            onChange={(e) => handleFieldEdit('CurrentRateType', e.target.value)}
                            style={{ padding: '4px', borderRadius: '4px', border: '1px solid #3b5bfd', width: '40%', fontSize: '13px' }}
                          >
                            {rateTypeOptions.map(opt => (
                              <option key={opt} value={opt}>{opt}</option>
                            ))}
                          </select>
                        </div>
                      ) : (
                        currentRateValue || <span style={{ color: '#9ca3af' }}>—</span>
                      )}
                    </span>
                  </div>

                  {/* Employment Type */}
                  <InfoRow
                    label="Employment Type"
                    value={c.EmploymentType}
                    isEditing={isEditing}
                    type="select"
                    options={employmentTypeOptions}
                    fieldName="EmploymentType"
                    onEdit={handleFieldEdit}
                  />

                  {/* Work Authorization */}
                  <InfoRow
                    label="Work Authorization"
                    value={c.WorkAuthorization}
                    isEditing={isEditing}
                    type="select"
                    options={workAuthOptions}
                    fieldName="WorkAuthorization"
                    onEdit={handleFieldEdit}
                  />

                  {/* Work Auth Petitioner */}
                  <div className="candidate-info-row" style={{ position: 'relative' }}>
                    <span className="candidate-info-label">Work Auth Petitioner</span>
                    <span className="candidate-info-value" style={{ position: 'relative' }}>
                      {isEditing ? (
                        <div style={{ position: 'relative', width: '100%' }}>
                          <input
                            type="text"
                            value={c.WorkAuthPetitioner || ''}
                            onChange={(e) => {
                              handleFieldEdit('WorkAuthPetitioner', e.target.value);
                              setPetitionerDropdownOpen(true);
                            }}
                            onFocus={() => {
                              if ((c.WorkAuthPetitioner || '').trim().length > 0) {
                                setPetitionerDropdownOpen(true);
                              }
                            }}
                            onBlur={() => {
                              setTimeout(() => setPetitionerDropdownOpen(false), 200);
                            }}
                            placeholder="Type to filter petitioner / employer"
                            className="edit-input"
                            autoComplete="off"
                            style={{
                              padding: '5px 8px',
                              borderRadius: '4px',
                              border: '1px solid #3b5bfd',
                              fontSize: '13px',
                              width: '100%',
                              outline: 'none'
                            }}
                          />
                          {petitionerDropdownOpen && (c.WorkAuthPetitioner || '').trim().length > 0 && (
                            <div
                              style={{
                                position: 'absolute',
                                top: '100%',
                                left: 0,
                                width: '100%',
                                minWidth: '280px',
                                maxHeight: '200px',
                                overflowY: 'auto',
                                background: '#fff',
                                border: '1px solid #cbd5e1',
                                borderRadius: '6px',
                                boxShadow: '0 4px 14px rgba(0,0,0,0.15)',
                                zIndex: 1050,
                                marginTop: '4px'
                              }}
                            >
                              {(() => {
                                const search = (c.WorkAuthPetitioner || '').trim().toLowerCase();
                                const filtered = (crmCompanies || [])
                                  .filter(comp => {
                                    const country = (comp.Country || comp.CountryName || comp.country || '').trim().toLowerCase();
                                    const disp = (comp.DisplayName || comp.CompanyName || '').trim();
                                    const isUS = country === 'us' || country === 'united states' || country === 'usa' || comp.CountryId === 2 || disp.includes('(US)') || disp.endsWith('(US)');
                                    const isIN = country === 'in' || country === 'india' || disp.includes('(IN)') || disp.endsWith('(IN)');
                                    return isUS && !isIN;
                                  })
                                  .map(comp => {
                                    const disp = (comp.DisplayName || comp.CompanyName || '').trim();
                                    const legal = (comp.CompanyName || '').trim();
                                    return {
                                      displayName: disp,
                                      companyName: legal && legal.toLowerCase() !== disp.toLowerCase() ? legal : ''
                                    };
                                  })
                                  .filter(item => item.displayName)
                                  .filter((item, idx, self) => self.findIndex(s => s.displayName.toLowerCase() === item.displayName.toLowerCase()) === idx)
                                  .filter(item =>
                                    item.displayName.toLowerCase().includes(search) ||
                                    item.companyName.toLowerCase().includes(search)
                                  );

                                if (filtered.length === 0) {
                                  return (
                                    <div style={{ padding: '8px 12px', fontSize: '12px', color: '#64748b' }}>
                                      No matching CRM companies. Custom entry <strong>"{c.WorkAuthPetitioner}"</strong> will be saved.
                                    </div>
                                  );
                                }

                                return filtered.map((item, idx) => (
                                  <div
                                    key={idx}
                                    onMouseDown={(e) => {
                                      e.preventDefault();
                                      handleFieldEdit('WorkAuthPetitioner', item.displayName);
                                      setPetitionerDropdownOpen(false);
                                    }}
                                    style={{
                                      display: 'flex',
                                      justifyContent: 'space-between',
                                      alignItems: 'center',
                                      padding: '8px 12px',
                                      cursor: 'pointer',
                                      borderBottom: '1px solid #f1f5f9',
                                      transition: 'background 0.15s ease'
                                    }}
                                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8fafc'}
                                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#fff'}
                                  >
                                    <span style={{ fontWeight: 600, color: '#1e293b', fontSize: '13px' }}>
                                      {item.displayName}
                                    </span>
                                    {item.companyName && (
                                      <span style={{ fontSize: '11px', color: '#64748b', marginLeft: '10px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {item.companyName}
                                      </span>
                                    )}
                                  </div>
                                ));
                              })()}
                            </div>
                          )}
                        </div>
                      ) : (
                        c.WorkAuthPetitioner ? (
                          <span style={{ fontWeight: 500, color: '#1e293b' }}>
                            {c.WorkAuthPetitioner}
                          </span>
                        ) : <span style={{ color: '#9ca3af' }}>—</span>
                      )}
                    </span>
                  </div>

                  {/* Work Auth Expiry */}
                  <div className="candidate-info-row">
                    <span className="candidate-info-label">Work Auth Expiry</span>
                    <span className="candidate-info-value">
                      {isEditing ? (
                        <input
                          type="date"
                          value={c.WorkAuthExpiryDate ? c.WorkAuthExpiryDate.split('T')[0] : ''}
                          onChange={(e) => handleFieldEdit('WorkAuthExpiryDate', e.target.value)}
                          className="edit-input"
                          style={{ padding: '4px 8px', borderRadius: '4px', border: '1px solid #3b5bfd', fontSize: '13px', width: '100%' }}
                        />
                      ) : (
                        c.WorkAuthExpiryDate ? (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
                            <Clock size={13} color="#6b7280" />
                            {formatDate(c.WorkAuthExpiryDate)}
                          </span>
                        ) : <span style={{ color: '#9ca3af' }}>—</span>
                      )}
                    </span>
                  </div>
                </div>
              </EditSection>

              {/* Application Card */}
              <EditSection isEditing={isEditing}>
                <div className="candidate-info-card">
                  <h3><Briefcase size={16} style={{ marginRight: 8 }} />Application</h3>
                  <div className="candidate-info-row">
                    <span className="candidate-info-label">Status</span>
                    <span className="candidate-info-value">
                      {isEditing ? (
                        <select
                          value={c.CandidateStatus}
                          onChange={(e) => handleFieldEdit('CandidateStatus', e.target.value)}
                          style={{ padding: '4px', borderRadius: '4px', border: '1px solid #3b5bfd' }}
                        >
                          {statusOptions.map(opt => (
                            <option key={opt} value={opt}>{opt}</option>
                          ))}
                        </select>
                      ) : (
                        <span className="cm-status-badge" style={{ background: statusStyle.bg, color: statusStyle.color }}>
                          {c.CandidateStatus}
                        </span>
                      )}
                    </span>
                  </div>
                  <InfoRow label="Job Title" value={c.JobTitle} isEditing={false} />
                  <InfoRow label="Created By" value={c.CreatedBy} isEditing={false} />
                  <InfoRow label="Created Date" value={formatDate(c.CreatedDt)} isEditing={false} />
                  {c.UpdatedDt && <InfoRow label="Last Updated" value={formatDate(c.UpdatedDt)} isEditing={false} />}
                </div>
              </EditSection>
            </div>

            {c.ProfileSummary && (
              <div className="candidate-skills-section">
                <h3><FileText size={16} style={{ marginRight: 8 }} />Profile Summary</h3>
                {isEditing ? (
                  <textarea
                    value={c.ProfileSummary || ''}
                    onChange={(e) => handleFieldEdit('ProfileSummary', e.target.value)}
                    rows={4}
                    style={{
                      width: '100%',
                      padding: '10px',
                      borderRadius: '4px',
                      border: '1px solid #3b5bfd',
                      fontSize: '14px',
                      lineHeight: '1.8',
                      color: '#374151'
                    }}
                  />
                ) : (
                  <p style={{ fontSize: 14, lineHeight: 1.8, color: '#374151', margin: 0 }}>{c.ProfileSummary}</p>
                )}
              </div>
            )}

            {c.Skills && c.Skills.length > 0 && (
              <div className="candidate-skills-section">
                <h3><Award size={16} style={{ marginRight: 8 }} />Skills ({c.Skills.length})</h3>
                <div className="candidate-skills-tags" style={{ gap: 8 }}>
                  {c.Skills.map((skill, i) => {
                    const sc = SKILL_TYPE_COLORS[skill.SkillType] || SKILL_TYPE_COLORS['HARD'];
                    return (
                      <span key={i} style={{
                        background: sc.bg, color: sc.color,
                        padding: '5px 14px', borderRadius: 20,
                        fontSize: 13, fontWeight: 600,
                        display: 'inline-flex', alignItems: 'center', gap: 5,
                        border: `1px solid ${sc.color}33`,
                      }}>
                        {skill.SkillName}
                      </span>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* BENCH TAB */}
        {activeTab === 'bench' && c.IsBench && (
          <div>
            <div className="candidate-info-grid">

              {/* Card 1: Bench Assignment & Priority */}
              <EditSection isEditing={isEditing}>
                <div className="candidate-info-card">
                  <h3 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Users size={16} color="#3b5bfd" />
                    Bench Assignment & Priority
                  </h3>

                  {/* Bench Recruiter (Multiple) */}
                  <div className="candidate-info-row" style={{ alignItems: 'flex-start' }}>
                    <span className="candidate-info-label" style={{ paddingTop: '6px' }}>Bench Recruiters</span>
                    <span className="candidate-info-value" style={{ width: '100%' }}>
                      {isEditing ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%' }}>
                          {/* Selected Recruiters Tags */}
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', minHeight: '28px' }}>
                            {((c.BenchAssignees && c.BenchAssignees.length > 0)
                              ? c.BenchAssignees
                              : (c.BenchAssignee ? [{ UserId: c.BenchAssigneeUserId || '', UserName: c.BenchAssignee, IsPrimary: 1 }] : [])
                            ).map((r, idx) => {
                              const isPrimary = r.IsPrimary === 1 || r.IsPrimary === true || idx === 0;
                              return (
                                <span
                                  key={r.UserName || idx}
                                  style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    padding: '3px 8px',
                                    borderRadius: '16px',
                                    fontSize: '12px',
                                    fontWeight: 600,
                                    background: isPrimary ? '#ecfdf5' : '#f1f5f9',
                                    color: isPrimary ? '#065f46' : '#334155',
                                    border: isPrimary ? '1px solid #a7f3d0' : '1px solid #cbd5e1'
                                  }}
                                >
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const current = [...(c.BenchAssignees || [])];
                                      const updated = current.map((item, i) => ({
                                        ...item,
                                        IsPrimary: i === idx ? 1 : 0
                                      }));
                                      handleFieldEdit('BenchAssignees', updated);
                                      handleFieldEdit('BenchAssignee', r.UserName);
                                      handleFieldEdit('BenchAssigneeUserId', r.UserId);
                                    }}
                                    title={isPrimary ? 'Primary Recruiter' : 'Click to make Primary'}
                                    style={{
                                      background: 'none',
                                      border: 'none',
                                      cursor: 'pointer',
                                      padding: 0,
                                      fontSize: '12px',
                                      lineHeight: 1
                                    }}
                                  >
                                    {isPrimary ? '⭐' : '☆'}
                                  </button>
                                  <span title={r.UserName}>{r.UserName.length > 15 ? `${r.UserName.substring(0, 15)}...` : r.UserName}</span>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const current = (c.BenchAssignees && c.BenchAssignees.length > 0)
                                        ? [...c.BenchAssignees]
                                        : (c.BenchAssignee ? [{ UserId: c.BenchAssigneeUserId || '', UserName: c.BenchAssignee, IsPrimary: 1 }] : []);
                                      const updated = current.filter((_, i) => i !== idx);
                                      if (isPrimary && updated.length > 0) {
                                        updated[0].IsPrimary = 1;
                                      }
                                      handleFieldEdit('BenchAssignees', updated);
                                      handleFieldEdit('BenchAssignee', updated[0]?.UserName || '');
                                      handleFieldEdit('BenchAssigneeUserId', updated[0]?.UserId || '');
                                    }}
                                    title="Remove Recruiter"
                                    style={{
                                      background: 'none',
                                      border: 'none',
                                      cursor: 'pointer',
                                      padding: '0 2px',
                                      fontSize: '14px',
                                      color: '#ef4444',
                                      fontWeight: 'bold',
                                      lineHeight: 1
                                    }}
                                  >
                                    ×
                                  </button>
                                </span>
                              );
                            })}
                          </div>

                          {/* Add Recruiter Dropdown */}
                          <select
                            value=""
                            onChange={(e) => {
                              const val = e.target.value;
                              if (!val) return;
                              const selectedUser = recruitersList.find(r => r.UserName === val);
                              const current = (c.BenchAssignees && c.BenchAssignees.length > 0)
                                ? [...c.BenchAssignees]
                                : (c.BenchAssignee ? [{ UserId: c.BenchAssigneeUserId || '', UserName: c.BenchAssignee, IsPrimary: 1 }] : []);

                              if (!current.some(r => r.UserName === val)) {
                                const isFirst = current.length === 0;
                                const updated = [
                                  ...current,
                                  {
                                    UserId: selectedUser?.UserId || '',
                                    UserName: val,
                                    IsPrimary: isFirst ? 1 : 0
                                  }
                                ];
                                handleFieldEdit('BenchAssignees', updated);
                                if (isFirst) {
                                  handleFieldEdit('BenchAssignee', val);
                                  handleFieldEdit('BenchAssigneeUserId', selectedUser?.UserId || '');
                                }
                              }
                            }}
                            className="edit-select"
                            style={{ padding: '5px 8px', borderRadius: '4px', border: '1px solid #3b5bfd', fontSize: '13px', width: '100%' }}
                          >
                            <option value="">+ Add Recruiter...</option>
                            {recruitersList
                              .filter(r => !(c.BenchAssignees || []).some(a => a.UserName === r.UserName))
                              .map(r => (
                                <option key={r.UserId || r.UserName} value={r.UserName}>{r.UserName}</option>
                              ))}
                          </select>
                        </div>
                      ) : (
                        (() => {
                          const list = (c.BenchAssignees && c.BenchAssignees.length > 0)
                            ? c.BenchAssignees
                            : (c.BenchAssignee ? [{ UserName: c.BenchAssignee, IsPrimary: 1 }] : []);
                          if (list.length === 0) return <span style={{ color: '#9ca3af' }}>—</span>;

                          const primary = list.find(r => typeof r === 'object' && (r.IsPrimary === 1 || r.IsPrimary === true)) || list[0];
                          const primaryName = typeof primary === 'string' ? primary : (primary.UserName || primary.name || '');
                          const remaining = list.filter(r => (typeof r === 'string' ? r : (r.UserName || r.name || '')) !== primaryName);
                          const displayName = primaryName.length > 15 ? `${primaryName.substring(0, 15)}...` : primaryName;
                          const allNames = list.map(r => typeof r === 'string' ? r : (r.UserName || r.name || '')).filter(Boolean).join(', ');

                          return (
                            <div
                              title={allNames}
                              style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', cursor: 'default' }}
                            >
                              <span
                                title={allNames}
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '4px',
                                  padding: '3px 8px',
                                  borderRadius: '14px',
                                  fontSize: '12px',
                                  fontWeight: 600,
                                  background: '#ecfdf5',
                                  color: '#065f46',
                                  border: '1px solid #a7f3d0',
                                  cursor: 'default'
                                }}
                              >
                                <span style={{ fontSize: '10px', background: '#ecfdf5', color: '#065f46', border: '1px solid #a7f3d0', padding: '1px 5px', borderRadius: '8px' }}>⭐ Primary</span>
                                <span>{displayName}</span>
                              </span>

                              {remaining.length > 0 && (
                                <span
                                  title={allNames}
                                  style={{
                                    fontSize: '11px',
                                    fontWeight: 700,
                                    background: '#f1f5f9',
                                    color: '#475569',
                                    border: '1px solid #cbd5e1',
                                    padding: '2px 8px',
                                    borderRadius: '12px',
                                    cursor: 'pointer',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    lineHeight: 1.3
                                  }}
                                >
                                  +{remaining.length}
                                </span>
                              )}
                            </div>
                          );
                        })()
                      )}
                    </span>
                  </div>

                  {/* Bench Priority */}
                  <div className="candidate-info-row">
                    <span className="candidate-info-label">Bench Priority</span>
                    <span className="candidate-info-value">
                      {isEditing ? (
                        <select
                          value={c.BenchPriority || ''}
                          onChange={(e) => handleFieldEdit('BenchPriority', e.target.value)}
                          className="edit-select"
                          style={{ padding: '4px 8px', borderRadius: '4px', border: '1px solid #3b5bfd', fontSize: '13px', width: '100%' }}
                        >
                          <option value="">Select Priority</option>
                          <option value="CRITICAL">Critical</option>
                          <option value="HIGH">High</option>
                          <option value="MEDIUM">Medium</option>
                          <option value="LOW">Low</option>
                        </select>
                      ) : (
                        c.BenchPriority ? (
                          <span style={{
                            padding: '3px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: 600,
                            background: BENCH_PRIORITY_COLORS[c.BenchPriority]?.bg || '#f3f4f6',
                            color: BENCH_PRIORITY_COLORS[c.BenchPriority]?.color || '#374151'
                          }}>
                            {c.BenchPriority === 'CRITICAL' ? '🔥 Critical' : c.BenchPriority === 'HIGH' ? '🔴 High' : c.BenchPriority === 'MEDIUM' ? '🟡 Medium' : '🟢 Low'}
                          </span>
                        ) : <span style={{ color: '#9ca3af' }}>—</span>
                      )}
                    </span>
                  </div>

                  {/* Reference / Source */}
                  <InfoRow
                    label="Reference / Source"
                    value={c.ReferenceText}
                    isEditing={isEditing}
                    fieldName="ReferenceText"
                    onEdit={handleFieldEdit}
                  />

                  {/* Internal Remarks */}
                  <div className="candidate-info-row">
                    <span className="candidate-info-label">Internal Remarks</span>
                    <span className="candidate-info-value">
                      {isEditing ? (
                        <textarea
                          value={c.Remarks || ''}
                          onChange={(e) => handleFieldEdit('Remarks', e.target.value)}
                          placeholder="Bench strategy notes..."
                          rows={2}
                          style={{
                            width: '100%', padding: '6px 8px', borderRadius: '4px',
                            border: '1px solid #3b5bfd', fontSize: '13px', resize: 'vertical'
                          }}
                        />
                      ) : (
                        c.Remarks ? (
                          <span style={{ fontSize: '13px', color: '#374151' }}>{c.Remarks}</span>
                        ) : <span style={{ color: '#9ca3af' }}>—</span>
                      )}
                    </span>
                  </div>
                </div>
              </EditSection>

              {/* Card 2: Timing, Aging & Availability */}
              <EditSection isEditing={isEditing}>
                <div className="candidate-info-card">
                  <h3 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Clock size={16} color="#3b5bfd" />
                    Timing, Aging & Availability
                  </h3>

                  {/* Days on Bench */}
                  <div className="candidate-info-row">
                    <span className="candidate-info-label">Days on Bench</span>
                    <span className="candidate-info-value">
                      {isEditing ? (
                        <input
                          type="date"
                          value={c.OutOfJobSince ? c.OutOfJobSince.split('T')[0] : ''}
                          onChange={(e) => handleFieldEdit('OutOfJobSince', e.target.value)}
                          className="edit-input"
                          style={{ padding: '4px 8px', borderRadius: '4px', border: '1px solid #3b5bfd', fontSize: '13px', width: '100%' }}
                        />
                      ) : (
                        (() => {
                          const aging = getBenchAgingStatus(c.OutOfJobSince, c.CreatedDt, c.DaysOnBench);
                          return (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                              <span style={{
                                padding: '2px 8px', borderRadius: '10px', fontSize: '12px', fontWeight: 600,
                                background: aging.bg, color: aging.color
                              }}>
                                {aging.icon} {aging.days} days ({aging.label})
                              </span>
                              {c.OutOfJobSince && (
                                <span style={{ fontSize: '11px', color: '#6b7280' }}>
                                  (Since {formatDate(c.OutOfJobSince)})
                                </span>
                              )}
                            </span>
                          );
                        })()
                      )}
                    </span>
                  </div>

                  {/* Out of Job Since */}
                  <div className="candidate-info-row">
                    <span className="candidate-info-label">Out of Job Since</span>
                    <span className="candidate-info-value">
                      {isEditing ? (
                        <input
                          type="date"
                          value={c.OutOfJobSince ? c.OutOfJobSince.split('T')[0] : ''}
                          onChange={(e) => handleFieldEdit('OutOfJobSince', e.target.value)}
                          className="edit-input"
                          style={{ padding: '4px 8px', borderRadius: '4px', border: '1px solid #3b5bfd', fontSize: '13px', width: '100%' }}
                        />
                      ) : (
                        c.OutOfJobSince ? (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', fontWeight: 500 }}>
                            <Calendar size={13} color="#3b5bfd" />
                            {formatDate(c.OutOfJobSince)}
                          </span>
                        ) : <span style={{ color: '#9ca3af' }}>—</span>
                      )}
                    </span>
                  </div>

                  {/* Placement Deadline */}
                  <div className="candidate-info-row">
                    <span className="candidate-info-label">Placement Deadline</span>
                    <span className="candidate-info-value">
                      {isEditing ? (
                        <input
                          type="date"
                          value={c.NewJobDeadline ? c.NewJobDeadline.split('T')[0] : ''}
                          onChange={(e) => handleFieldEdit('NewJobDeadline', e.target.value)}
                          className="edit-input"
                          style={{ padding: '4px 8px', borderRadius: '4px', border: '1px solid #3b5bfd', fontSize: '13px', width: '100%' }}
                        />
                      ) : (
                        c.NewJobDeadline ? (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', fontWeight: 500 }}>
                            <Calendar size={13} color="#3b5bfd" />
                            {formatDate(c.NewJobDeadline)}
                          </span>
                        ) : <span style={{ color: '#9ca3af' }}>—</span>
                      )}
                    </span>
                  </div>

                  {/* Recent Placement Date */}
                  <div className="candidate-info-row">
                    <span className="candidate-info-label">Recent Placement Date</span>
                    <span className="candidate-info-value">
                      {isEditing ? (
                        <input
                          type="date"
                          value={c.RecentPlacementDate ? c.RecentPlacementDate.split('T')[0] : ''}
                          onChange={(e) => handleFieldEdit('RecentPlacementDate', e.target.value)}
                          className="edit-input"
                          style={{ padding: '4px 8px', borderRadius: '4px', border: '1px solid #3b5bfd', fontSize: '13px', width: '100%' }}
                        />
                      ) : (
                        c.RecentPlacementDate ? (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', fontWeight: 500 }}>
                            <Calendar size={13} color="#3b5bfd" />
                            {formatDate(c.RecentPlacementDate)}
                          </span>
                        ) : <span style={{ color: '#9ca3af' }}>—</span>
                      )}
                    </span>
                  </div>

                  {/* Notice Period */}
                  <InfoRow
                    label="Notice Period"
                    value={c.NoticePeriod}
                    isEditing={isEditing}
                    type="select"
                    options={noticePeriodOptions}
                    fieldName="NoticePeriod"
                    onEdit={handleFieldEdit}
                  />

                  {/* Available From */}
                  <div className="candidate-info-row">
                    <span className="candidate-info-label">Available From</span>
                    <span className="candidate-info-value">
                      {isEditing ? (
                        <input
                          type="date"
                          value={c.AvailableFromDate ? c.AvailableFromDate.split('T')[0] : ''}
                          onChange={(e) => handleFieldEdit('AvailableFromDate', e.target.value)}
                          className="edit-input"
                          style={{ padding: '4px 8px', borderRadius: '4px', border: '1px solid #3b5bfd', fontSize: '13px', width: '100%' }}
                        />
                      ) : (
                        c.AvailableFromDate ? (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', fontWeight: 500 }}>
                            <Calendar size={13} color="#3b5bfd" />
                            {formatDate(c.AvailableFromDate)}
                          </span>
                        ) : <span style={{ color: '#9ca3af' }}>—</span>
                      )}
                    </span>
                  </div>
                </div>
              </EditSection>

              {/* Card 3: Immigration & Work Authorization */}
              <EditSection isEditing={isEditing}>
                <div className="candidate-info-card">
                  <h3 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Shield size={16} color="#3b5bfd" />
                    Immigration & Work Auth
                  </h3>

                  {/* Work Authorization */}
                  <InfoRow
                    label="Work Authorization"
                    value={c.WorkAuthorization}
                    isEditing={isEditing}
                    type="select"
                    options={workAuthOptions}
                    fieldName="WorkAuthorization"
                    onEdit={handleFieldEdit}
                  />

                  {/* Work Auth Petitioner */}
                  <div className="candidate-info-row" style={{ position: 'relative' }}>
                    <span className="candidate-info-label">Work Auth Petitioner</span>
                    <span className="candidate-info-value" style={{ position: 'relative' }}>
                      {isEditing ? (
                        <div style={{ position: 'relative', width: '100%' }}>
                          <input
                            type="text"
                            value={c.WorkAuthPetitioner || ''}
                            onChange={(e) => {
                              handleFieldEdit('WorkAuthPetitioner', e.target.value);
                              setPetitionerDropdownOpen(true);
                            }}
                            onFocus={() => {
                              if ((c.WorkAuthPetitioner || '').trim().length > 0) {
                                setPetitionerDropdownOpen(true);
                              }
                            }}
                            onBlur={() => {
                              setTimeout(() => setPetitionerDropdownOpen(false), 200);
                            }}
                            placeholder="Type to filter petitioner / employer"
                            className="edit-input"
                            autoComplete="off"
                            style={{
                              padding: '5px 8px',
                              borderRadius: '4px',
                              border: '1px solid #3b5bfd',
                              fontSize: '13px',
                              width: '100%',
                              outline: 'none'
                            }}
                          />
                          {petitionerDropdownOpen && (c.WorkAuthPetitioner || '').trim().length > 0 && (
                            <div
                              style={{
                                position: 'absolute',
                                top: '100%',
                                left: 0,
                                width: '100%',
                                minWidth: '280px',
                                maxHeight: '200px',
                                overflowY: 'auto',
                                background: '#fff',
                                border: '1px solid #cbd5e1',
                                borderRadius: '6px',
                                boxShadow: '0 4px 14px rgba(0,0,0,0.15)',
                                zIndex: 1050,
                                marginTop: '4px'
                              }}
                            >
                              {(() => {
                                const search = (c.WorkAuthPetitioner || '').trim().toLowerCase();
                                const filtered = (crmCompanies || [])
                                  .filter(comp => {
                                    const country = (comp.Country || comp.CountryName || comp.country || '').trim().toLowerCase();
                                    const disp = (comp.DisplayName || comp.CompanyName || '').trim();
                                    const isUS = country === 'us' || country === 'united states' || country === 'usa' || comp.CountryId === 2 || disp.includes('(US)') || disp.endsWith('(US)');
                                    const isIN = country === 'in' || country === 'india' || disp.includes('(IN)') || disp.endsWith('(IN)');
                                    return isUS && !isIN;
                                  })
                                  .map(comp => {
                                    const disp = (comp.DisplayName || comp.CompanyName || '').trim();
                                    const legal = (comp.CompanyName || '').trim();
                                    return {
                                      displayName: disp,
                                      companyName: legal && legal.toLowerCase() !== disp.toLowerCase() ? legal : ''
                                    };
                                  })
                                  .filter(item => item.displayName)
                                  .filter((item, idx, self) => self.findIndex(s => s.displayName.toLowerCase() === item.displayName.toLowerCase()) === idx)
                                  .filter(item =>
                                    item.displayName.toLowerCase().includes(search) ||
                                    item.companyName.toLowerCase().includes(search)
                                  );

                                if (filtered.length === 0) {
                                  return (
                                    <div style={{ padding: '8px 12px', fontSize: '12px', color: '#64748b' }}>
                                      No matching CRM companies. Custom entry <strong>"{c.WorkAuthPetitioner}"</strong> will be saved.
                                    </div>
                                  );
                                }

                                return filtered.map((item, idx) => (
                                  <div
                                    key={idx}
                                    onMouseDown={(e) => {
                                      e.preventDefault();
                                      handleFieldEdit('WorkAuthPetitioner', item.displayName);
                                      setPetitionerDropdownOpen(false);
                                    }}
                                    style={{
                                      display: 'flex',
                                      justifyContent: 'space-between',
                                      alignItems: 'center',
                                      padding: '8px 12px',
                                      cursor: 'pointer',
                                      borderBottom: '1px solid #f1f5f9',
                                      transition: 'background 0.15s ease'
                                    }}
                                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8fafc'}
                                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#fff'}
                                  >
                                    <span style={{ fontWeight: 600, color: '#1e293b', fontSize: '13px' }}>
                                      {item.displayName}
                                    </span>
                                    {item.companyName && (
                                      <span style={{ fontSize: '11px', color: '#64748b', marginLeft: '10px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {item.companyName}
                                      </span>
                                    )}
                                  </div>
                                ));
                              })()}
                            </div>
                          )}
                        </div>
                      ) : (
                        c.WorkAuthPetitioner ? (
                          <span style={{ fontWeight: 500, color: '#1e293b' }}>
                            {c.WorkAuthPetitioner}
                          </span>
                        ) : <span style={{ color: '#9ca3af' }}>—</span>
                      )}
                    </span>
                  </div>

                  {/* Work Auth Expiry */}
                  <div className="candidate-info-row">
                    <span className="candidate-info-label">Work Auth Expiry</span>
                    <span className="candidate-info-value">
                      {isEditing ? (
                        <input
                          type="date"
                          value={c.WorkAuthExpiryDate ? c.WorkAuthExpiryDate.split('T')[0] : ''}
                          onChange={(e) => handleFieldEdit('WorkAuthExpiryDate', e.target.value)}
                          className="edit-input"
                          style={{ padding: '4px 8px', borderRadius: '4px', border: '1px solid #3b5bfd', fontSize: '13px', width: '100%' }}
                        />
                      ) : (
                        c.WorkAuthExpiryDate ? (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
                            <Clock size={13} color="#6b7280" />
                            {formatDate(c.WorkAuthExpiryDate)}
                          </span>
                        ) : <span style={{ color: '#9ca3af' }}>—</span>
                      )}
                    </span>
                  </div>

                  {/* H1B Transfer Willing */}
                  <div className="candidate-info-row">
                    <span className="candidate-info-label">H-1B Transfer Willing</span>
                    <span className="candidate-info-value">
                      {isEditing ? (
                        <select
                          value={c.H1BTransferWilling ? 'true' : 'false'}
                          onChange={(e) => handleFieldEdit('H1BTransferWilling', e.target.value === 'true')}
                          className="edit-select"
                          style={{ padding: '4px 8px', borderRadius: '4px', border: '1px solid #3b5bfd', fontSize: '13px', width: '100%' }}
                        >
                          <option value="true">Yes</option>
                          <option value="false">No</option>
                        </select>
                      ) : (
                        <span style={{ fontWeight: 500, color: c.H1BTransferWilling ? '#059669' : '#6b7280' }}>
                          {c.H1BTransferWilling ? '✅ Yes' : '❌ No'}
                        </span>
                      )}
                    </span>
                  </div>

                  {/* I-140 Status */}
                  <InfoRow
                    label="I-140 Status"
                    value={c.I140Status}
                    isEditing={isEditing}
                    type="select"
                    options={i140Options}
                    fieldName="I140Status"
                    onEdit={handleFieldEdit}
                  />

                  {/* I-140 Priority Date */}
                  <div className="candidate-info-row">
                    <span className="candidate-info-label">I-140 Priority Date</span>
                    <span className="candidate-info-value">
                      {isEditing ? (
                        <input
                          type="date"
                          value={c.I140PriorityDate ? c.I140PriorityDate.split('T')[0] : ''}
                          onChange={(e) => handleFieldEdit('I140PriorityDate', e.target.value)}
                          className="edit-input"
                          style={{ padding: '4px 8px', borderRadius: '4px', border: '1px solid #3b5bfd', fontSize: '13px', width: '100%' }}
                        />
                      ) : (
                        c.I140PriorityDate ? (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
                            <Calendar size={13} color="#3b5bfd" />
                            {formatDate(c.I140PriorityDate)}
                          </span>
                        ) : <span style={{ color: '#9ca3af' }}>—</span>
                      )}
                    </span>
                  </div>
                </div>
              </EditSection>

              {/* Card 4: Commercial Rates & C2C Vendor */}
              <EditSection isEditing={isEditing}>
                <div className="candidate-info-card">
                  <h3 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <DollarSign size={16} color="#3b5bfd" />
                    Commercial & C2C Details
                  </h3>

                  {/* Expected Rate */}
                  <div className="candidate-info-row">
                    <span className="candidate-info-label">Expected Rate</span>
                    <span className="candidate-info-value">
                      {isEditing ? (
                        <div style={{ display: 'flex', gap: '5px', width: '100%' }}>
                          <div style={{ position: 'relative', width: '30%' }}>
                            <span style={{ position: 'absolute', left: '8px', top: '50%', transform: 'translateY(-50%)', fontSize: '13px', color: '#6b7280' }}>
                              {currencySymbol}
                            </span>
                            <input
                              type="number"
                              value={c.ExpectedRateFrom || ''}
                              onChange={(e) => handleFieldEdit('ExpectedRateFrom', e.target.value)}
                              placeholder="From"
                              style={{ padding: '4px 4px 4px 24px', borderRadius: '4px', border: '1px solid #3b5bfd', width: '100%', fontSize: '13px' }}
                            />
                          </div>
                          <div style={{ position: 'relative', width: '30%' }}>
                            <span style={{ position: 'absolute', left: '8px', top: '50%', transform: 'translateY(-50%)', fontSize: '13px', color: '#6b7280' }}>
                              {currencySymbol}
                            </span>
                            <input
                              type="number"
                              value={c.ExpectedRateTo || ''}
                              onChange={(e) => handleFieldEdit('ExpectedRateTo', e.target.value)}
                              placeholder="To"
                              style={{ padding: '4px 4px 4px 24px', borderRadius: '4px', border: '1px solid #3b5bfd', width: '100%', fontSize: '13px' }}
                            />
                          </div>
                          <select
                            value={c.ExpectedRateType || 'Hourly'}
                            onChange={(e) => handleFieldEdit('ExpectedRateType', e.target.value)}
                            style={{ padding: '4px', borderRadius: '4px', border: '1px solid #3b5bfd', width: '40%', fontSize: '13px' }}
                          >
                            {rateTypeOptions.map(opt => (
                              <option key={opt} value={opt}>{opt}</option>
                            ))}
                          </select>
                        </div>
                      ) : (
                        expectedRateRange || <span style={{ color: '#9ca3af' }}>—</span>
                      )}
                    </span>
                  </div>

                  {/* Current / Floor Rate */}
                  <div className="candidate-info-row">
                    <span className="candidate-info-label">Current / Floor Rate</span>
                    <span className="candidate-info-value">
                      {isEditing ? (
                        <div style={{ display: 'flex', gap: '5px', width: '100%' }}>
                          <div style={{ position: 'relative', width: '60%' }}>
                            <span style={{ position: 'absolute', left: '8px', top: '50%', transform: 'translateY(-50%)', fontSize: '13px', color: '#6b7280' }}>
                              {currencySymbol}
                            </span>
                            <input
                              type="number"
                              value={c.CurrentRate || ''}
                              onChange={(e) => handleFieldEdit('CurrentRate', e.target.value)}
                              placeholder="Current Rate"
                              style={{ padding: '4px 4px 4px 24px', borderRadius: '4px', border: '1px solid #3b5bfd', width: '100%', fontSize: '13px' }}
                            />
                          </div>
                          <select
                            value={c.CurrentRateType || 'Hourly'}
                            onChange={(e) => handleFieldEdit('CurrentRateType', e.target.value)}
                            style={{ padding: '4px', borderRadius: '4px', border: '1px solid #3b5bfd', width: '40%', fontSize: '13px' }}
                          >
                            {rateTypeOptions.map(opt => (
                              <option key={opt} value={opt}>{opt}</option>
                            ))}
                          </select>
                        </div>
                      ) : (
                        currentRateValue || <span style={{ color: '#9ca3af' }}>—</span>
                      )}
                    </span>
                  </div>

                  {/* Employment Type */}
                  <InfoRow
                    label="Employment Type"
                    value={c.EmploymentType}
                    isEditing={isEditing}
                    type="select"
                    options={employmentTypeOptions}
                    fieldName="EmploymentType"
                    onEdit={handleFieldEdit}
                  />

                  {/* C2C Corp Name */}
                  <InfoRow
                    label="C2C Corp Name"
                    value={c.C2CCorpName}
                    isEditing={isEditing}
                    fieldName="C2CCorpName"
                    onEdit={handleFieldEdit}
                  />

                  {/* C2C FEIN */}
                  <InfoRow
                    label="C2C FEIN / Tax ID"
                    value={c.C2CFEIN}
                    isEditing={isEditing}
                    fieldName="C2CFEIN"
                    onEdit={handleFieldEdit}
                  />

                  {/* C2C Structure */}
                  <InfoRow
                    label="C2C Structure"
                    value={c.C2CStructure}
                    isEditing={isEditing}
                    type="select"
                    options={c2cStructureOptions}
                    fieldName="C2CStructure"
                    onEdit={handleFieldEdit}
                  />
                </div>
              </EditSection>

              {/* Card 5: Project History & Reason for Leaving */}
              <EditSection isEditing={isEditing}>
                <div className="candidate-info-card">
                  <h3 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Briefcase size={16} color="#3b5bfd" />
                    Project History & Exit
                  </h3>

                  {/* Currently Working */}
                  <div className="candidate-info-row">
                    <span className="candidate-info-label">Currently Employed</span>
                    <span className="candidate-info-value">
                      {isEditing ? (
                        <select
                          value={c.IsCurrentlyWorking ? 'true' : 'false'}
                          onChange={(e) => handleFieldEdit('IsCurrentlyWorking', e.target.value === 'true')}
                          className="edit-select"
                          style={{ padding: '4px 8px', borderRadius: '4px', border: '1px solid #3b5bfd', fontSize: '13px', width: '100%' }}
                        >
                          <option value="true">Yes</option>
                          <option value="false">No</option>
                        </select>
                      ) : (
                        <span style={{ fontWeight: 500, color: c.IsCurrentlyWorking ? '#059669' : '#6b7280' }}>
                          {c.IsCurrentlyWorking ? 'Yes' : 'No'}
                        </span>
                      )}
                    </span>
                  </div>

                  {/* Current / Last Employer */}
                  <InfoRow
                    label="Current/Last Employer"
                    value={c.CurrentEmployer}
                    isEditing={isEditing}
                    fieldName="CurrentEmployer"
                    onEdit={handleFieldEdit}
                  />

                  {/* Current / Last Client */}
                  <InfoRow
                    label="Last End Client"
                    value={c.CurrentClientName}
                    isEditing={isEditing}
                    fieldName="CurrentClientName"
                    onEdit={handleFieldEdit}
                  />

                  {/* Reason for Leaving */}
                  <div className="candidate-info-row">
                    <span className="candidate-info-label">Reason for Leaving</span>
                    <span className="candidate-info-value">
                      {isEditing ? (
                        <textarea
                          value={c.ReasonForLeaving || ''}
                          onChange={(e) => handleFieldEdit('ReasonForLeaving', e.target.value)}
                          placeholder="Reason for leaving previous project..."
                          rows={2}
                          style={{
                            width: '100%', padding: '6px 8px', borderRadius: '4px',
                            border: '1px solid #3b5bfd', fontSize: '13px', resize: 'vertical'
                          }}
                        />
                      ) : (
                        c.ReasonForLeaving ? (
                          <span style={{ fontSize: '13px', color: '#374151' }}>{c.ReasonForLeaving}</span>
                        ) : <span style={{ color: '#9ca3af' }}>—</span>
                      )}
                    </span>
                  </div>
                </div>
              </EditSection>

              {/* Card 6: Mobility & Work Preferences */}
              <EditSection isEditing={isEditing}>
                <div className="candidate-info-card">
                  <h3 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <MapPin size={16} color="#3b5bfd" />
                    Mobility & Work Preferences
                  </h3>

                  {/* Remote Status */}
                  <InfoRow
                    label="Remote Status"
                    value={c.RemoteStatus}
                    displayValue={
                      <span className="cm-status-badge" style={{ background: remoteStyle.bg, color: remoteStyle.color }}>
                        {c.RemoteStatus === 'OnSite' ? 'On-Site' : c.RemoteStatus}
                      </span>
                    }
                    isEditing={isEditing}
                    fieldName="RemoteStatus"
                    onEdit={handleFieldEdit}
                    options={remoteOptions}
                  />

                  {/* Willing to Relocate */}
                  <div className="candidate-info-row">
                    <span className="candidate-info-label">Willing to Relocate</span>
                    <span className="candidate-info-value">
                      {isEditing ? (
                        <select
                          value={c.WillingToRelocate ? 'true' : 'false'}
                          onChange={(e) => handleFieldEdit('WillingToRelocate', e.target.value === 'true')}
                          className="edit-select"
                          style={{ padding: '4px 8px', borderRadius: '4px', border: '1px solid #3b5bfd', fontSize: '13px', width: '100%' }}
                        >
                          <option value="true">Yes</option>
                          <option value="false">No</option>
                        </select>
                      ) : (
                        <span style={{ fontWeight: 500, color: c.WillingToRelocate ? '#059669' : '#6b7280' }}>
                          {c.WillingToRelocate ? '✅ Yes' : '❌ No'}
                        </span>
                      )}
                    </span>
                  </div>

                  {/* Relocation Notes */}
                  <InfoRow
                    label="Relocation Notes"
                    value={c.RelocationNotes}
                    isEditing={isEditing}
                    fieldName="RelocationNotes"
                    onEdit={handleFieldEdit}
                  />

                  {/* Willing to Travel */}
                  <div className="candidate-info-row">
                    <span className="candidate-info-label">Willing to Travel</span>
                    <span className="candidate-info-value">
                      {isEditing ? (
                        <select
                          value={c.WillingToTravel ? 'true' : 'false'}
                          onChange={(e) => handleFieldEdit('WillingToTravel', e.target.value === 'true')}
                          className="edit-select"
                          style={{ padding: '4px 8px', borderRadius: '4px', border: '1px solid #3b5bfd', fontSize: '13px', width: '100%' }}
                        >
                          <option value="true">Yes</option>
                          <option value="false">No</option>
                        </select>
                      ) : (
                        <span style={{ fontWeight: 500, color: c.WillingToTravel ? '#059669' : '#6b7280' }}>
                          {c.WillingToTravel ? '✅ Yes' : '❌ No'}
                        </span>
                      )}
                    </span>
                  </div>
                </div>
              </EditSection>

            </div>
          </div>
        )}

        {/* EXPERIENCE */}
        {activeTab === 'experience' && (
          <div>
            <h3 style={{ fontSize: 18, marginBottom: 10, color: '#111' }}>
              Work Experience
              {c.WorkExperience && c.WorkExperience.length > 0 && (
                <span style={{ fontSize: 13, fontWeight: 500, color: '#6b7280', marginLeft: 8 }}>
                  ({c.WorkExperience.length} {c.WorkExperience.length === 1 ? 'role' : 'roles'})
                </span>
              )}
            </h3>
            {isEditing ? (
              <WorkExperienceEditor
                experiences={c.WorkExperience || []}
                onChange={(newExperiences) => handleFieldEdit('WorkExperience', newExperiences)}
              />
            ) : (
              c.WorkExperience && c.WorkExperience.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  {c.WorkExperience.map((exp, i) => (
                    <div key={i} style={{
                      background: '#f8f9fa', borderRadius: 10, padding: '20px 24px',
                      border: '1px solid #e5e7eb', borderLeft: '4px solid #3b5bfd',
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
                        <div>
                          <div style={{ fontWeight: 700, fontSize: 16, color: '#111' }}>{exp.JobTitle || '—'}</div>
                        </div>
                        <div style={{ fontSize: 13, color: '#6b7280', fontWeight: 500, textAlign: 'right', display: 'flex', alignItems: 'center', gap: 6 }}>
                          {exp.Company && (
                            <span style={{ color: '#3b5bfd', fontWeight: 600, fontSize: 14 }}>{exp.Company}</span>
                          )}
                          {exp.Company && <span style={{ color: '#9ca3af' }}>|</span>}
                          <span>
                            {formatMonthYear(exp.StartDate)} — {exp.IsCurrent
                              ? <span style={{ color: '#3b5bfd', fontWeight: 700 }}>Present</span>
                              : formatMonthYear(exp.EndDate)}
                          </span>
                          {exp.IsCurrent && (
                            <div style={{ fontSize: 11, color: '#3b5bfd', marginTop: 2 }}>Current Role</div>
                          )}
                        </div>
                      </div>
                      {exp.Description && (
                        <p style={{ fontSize: 14, color: '#555', lineHeight: 1.7, margin: 0 }}>{exp.Description}</p>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p style={{ color: '#9ca3af', fontStyle: 'italic' }}>No work experience recorded.</p>
              )
            )}
          </div>
        )}

        {/* EDUCATION */}
        {activeTab === 'education' && (
          <div>
            <h3 style={{ fontSize: 18, marginBottom: 10, color: '#111' }}>
              <GraduationCap size={18} style={{ marginRight: 8, verticalAlign: 'middle' }} />
              Education
            </h3>
            {isEditing ? (
              <EducationEditor
                educations={c.Education || []}
                onChange={(newEducation) => handleFieldEdit('Education', newEducation)}
              />
            ) : (
              c.Education && c.Education.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginBottom: 16 }}>
                  {c.Education.map((edu, i) => (
                    <div key={i} style={{ background: '#f8f9fa', borderRadius: 10, padding: '18px 24px', border: '1px solid #e5e7eb' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 8 }}>
                        <div style={{ fontWeight: 700, fontSize: 15, color: '#111' }}>
                          {edu.Degree}{edu.FieldOfStudy ? ` in ${edu.FieldOfStudy}` : ''}
                        </div>
                        <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 500, whiteSpace: 'nowrap' }}>
                          {formatMonthYear(edu.StartDate)} — {formatMonthYear(edu.EndDate)}
                        </div>
                      </div>
                      <div style={{ fontSize: 13, color: '#3b5bfd', fontWeight: 600, marginTop: 4 }}>{edu.Institution}</div>
                      {edu.GPA && <div style={{ fontSize: 12, color: '#374151', fontWeight: 600, marginTop: 4 }}>GPA: {edu.GPA}</div>}
                    </div>
                  ))}
                </div>
              ) : (
                <p style={{ color: '#9ca3af', fontStyle: 'italic', marginBottom: 28 }}>No education recorded.</p>
              )
            )}

            <h3 style={{ fontSize: 18, marginBottom: 16, color: '#111' }}>
              <Award size={18} style={{ marginRight: 8, verticalAlign: 'middle' }} />
              Certifications
            </h3>
            {isEditing ? (
              <CertificationEditor
                certifications={c.Certifications || []}
                onChange={(newCerts) => handleFieldEdit('Certifications', newCerts)}
              />
            ) : (
              c.Certifications && c.Certifications.length > 0 ? (
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, 1fr)',
                  gap: '14px'
                }}>
                  {c.Certifications.map((cert, i) => (
                    <div key={i} style={{
                      background: '#f0fdf4',
                      borderRadius: 10,
                      padding: '18px 20px',
                      border: '1px solid #bbf7d0',
                      height: 'fit-content'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 8 }}>
                        <div style={{ fontWeight: 700, fontSize: 15, color: '#111' }}>{cert.CertificationName}</div>
                        <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 500, display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}>
                          {cert.IssuingOrganization && (
                            <span style={{ color: '#065f46', fontWeight: 600, fontSize: 13 }}>{cert.IssuingOrganization}</span>
                          )}
                          {cert.IssuingOrganization && <span style={{ color: '#9ca3af' }}>|</span>}
                          <span>Issued: {formatMonthYear(cert.IssueDate) || '—'}</span>
                        </div>
                      </div>
                      {cert.ExpiryDate && <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>Expires: {formatMonthYear(cert.ExpiryDate)}</div>}
                      {cert.CredentialId && <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>ID: {cert.CredentialId}</div>}
                      {cert.CredentialUrl && (
                        <a href={cert.CredentialUrl} target="_blank" rel="noopener noreferrer"
                          style={{ fontSize: 12, color: '#3b5bfd', marginTop: 8, display: 'inline-flex', alignItems: 'center', gap: 4, textDecoration: 'none' }}>
                          Verify <ExternalLink size={11} />
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p style={{ color: '#9ca3af', fontStyle: 'italic' }}>No certifications recorded.</p>
              )
            )}
          </div>
        )}

        {/* RESUME */}
        {activeTab === 'resume' && (() => {
          const rawResumeDocs = c.Documents
            ? c.Documents.filter(doc => doc.DocumentType?.toUpperCase() === 'RESUME' || doc.IsPrimaryResume)
            : [];
          if (rawResumeDocs.length === 0 && c.Document && c.Document.FileNameOriginal) {
            rawResumeDocs.push(c.Document);
          }

          // Sort chronologically descending: current/newest date comes in front, older previous resumes back
          const resumeDocs = [...rawResumeDocs].sort((a, b) => {
            const dateA = new Date(a.UploadedOn || a.CreatedAt || a.CreatedDt || 0).getTime();
            const dateB = new Date(b.UploadedOn || b.CreatedAt || b.CreatedDt || 0).getTime();
            return dateB - dateA;
          });

          return (
            <div style={{ display: 'flex', gap: '20px', alignItems: 'flex-start' }}>
              <div style={{ flex: selectedResumeId ? '0 0 350px' : '1', transition: 'all 0.3s' }}>
                <h3 style={{ fontSize: 18, marginBottom: 10, color: '#111' }}>
                  <FileText size={18} style={{ marginRight: 8, verticalAlign: 'middle' }} />
                  Resume Document{resumeDocs.length > 1 ? 's' : ''}
                </h3>
                {resumeDocs.length > 0 ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {resumeDocs.map((doc, index) => (
                      <div key={doc.DocumentId || index} className="candidate-document-item" style={{
                        background: '#ffffff',
                        border: selectedResumeId === doc.DocumentId ? '2px solid #3b5bfd' : '1px solid #e5e7eb'
                      }}>
                        <FileText size={28} className="candidate-document-icon" />
                        <div className="candidate-document-info">
                          <div className="candidate-document-name" title={doc.FileNameOriginal || doc.DocumentName}>
                            {truncateText(doc.FileNameOriginal || doc.DocumentName)}
                          </div>
                          <div className="candidate-document-meta">
                            {doc.UploadedOn && (
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', color: '#374151', fontWeight: 500 }}>
                                <Calendar size={13} color="#3b5bfd" />
                                {formatDate(doc.UploadedOn)}
                              </span>
                            )}
                            {doc.FileSizeBytes && <span>Size: {formatFileSize(doc.FileSizeBytes)}</span>}
                            {doc.FileExtension && <span>Type: {doc.FileExtension.toUpperCase().replace('.', '')}</span>}
                            {doc.ParseStatus && <span>Status: {doc.ParseStatus}</span>}
                            {doc.IsPrimaryResume && <span style={{ color: '#3b5bfd', fontWeight: 600 }}>Primary</span>}
                          </div>
                        </div>
                        <div className="candidate-document-actions" style={{ display: 'flex', gap: '8px' }}>
                          <button
                            className="candidate-document-btn"
                            onClick={() => setSelectedResumeId(selectedResumeId === doc.DocumentId ? null : doc.DocumentId)}
                            title={selectedResumeId === doc.DocumentId ? "Close Preview" : "Preview Resume"}
                            style={{
                              background: selectedResumeId === doc.DocumentId ? '#3b5bfd' : 'transparent',
                              color: selectedResumeId === doc.DocumentId ? '#fff' : 'inherit'
                            }}
                          >
                            <Eye size={14} /> Preview
                          </button>
                          <button
                            className="candidate-document-btn"
                            onClick={() => handleDownload(doc.DocumentId, doc.FileNameOriginal)}
                            disabled={previewLoading}
                          >
                            <Download size={14} /> Download
                          </button>
                          {resumeDocs.length > 1 && (
                            <button
                              className="candidate-document-btn"
                              onClick={() => handleDeleteResume(doc.DocumentId, doc.FileNameOriginal || doc.DocumentName)}
                              title="Delete this resume"
                              style={{
                                color: '#ef4444',
                                borderColor: '#fca5a5'
                              }}
                            >
                              <Trash2 size={14} /> Delete
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '60px 20px', color: '#9ca3af' }}>
                    <FileText size={56} style={{ marginBottom: 14, opacity: 0.35 }} />
                    <p style={{ margin: 0, fontSize: 15 }}>No resume document available</p>
                  </div>
                )}
              </div>

              {selectedResumeId && (
                <div style={{ flex: '1', minWidth: '400px', height: '800px', border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden', background: '#fff' }}>
                  <div style={{ background: '#f9fafb', padding: '10px 16px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontWeight: 600, color: '#374151', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Eye size={16} color="#3b5bfd" />
                      Preview: {truncateText(resumeDocs.find(d => d.DocumentId === selectedResumeId)?.FileNameOriginal || '')}
                    </span>
                    <button
                      onClick={() => setSelectedResumeId(null)}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280' }}
                    >
                      <X size={18} />
                    </button>
                  </div>
                  <iframe
                    src={`${BASE_URL}/api/resumes/${selectedResumeId}/preview?token=${localStorage.getItem('token')}`}
                    style={{ width: '100%', height: 'calc(100% - 44px)', border: 'none' }}
                    title="Resume Preview"
                  />
                </div>
              )}
            </div>
          );
        })()}

        {/* DOCUMENTS */}
        {activeTab === 'documents' && (
          <div style={{ display: 'flex', gap: '20px', alignItems: 'flex-start' }}>
            <div style={{ flex: selectedResumeId ? '0 0 350px' : '1', transition: 'all 0.3s' }}>
              <h3 style={{ fontSize: 18, marginBottom: 16, color: '#111' }}>
                <FileText size={18} style={{ marginRight: 8, verticalAlign: 'middle' }} />
                Additional Documents ({nonResumeDocs.length})
              </h3>

              {isEditing ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  <div style={{ padding: '12px', background: '#fef3c7', borderRadius: '8px', border: '1px solid #fde68a', marginBottom: '16px', fontSize: '13px', color: '#92400e' }}>
                    <strong>Note:</strong> Resumes are managed in the dedicated "Resume" tab.
                  </div>
                  <DocumentEditor
                    documents={nonResumeDocs}
                    onChange={(updatedNonResumes) => {
                      // When editing additional docs, we must preserve the resumes in the original candidate state
                      const otherDocs = (c.Documents || []).filter(doc =>
                        doc.DocumentType?.toUpperCase() === 'RESUME' || doc.IsPrimaryResume
                      );
                      handleFieldEdit('Documents', [...otherDocs, ...updatedNonResumes]);
                    }}
                    candidateId={id}
                  />
                </div>
              ) : (
                nonResumeDocs.length > 0 ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {nonResumeDocs.map((doc, index) => (
                      <div key={doc.DocumentId || index} className="candidate-document-item" style={{
                        background: '#ffffff',
                        border: selectedResumeId === doc.DocumentId ? '2px solid #3b5bfd' : '1px solid #e5e7eb'
                      }}>
                        <FileText size={28} className="candidate-document-icon" />
                        <div className="candidate-document-info">
                          <div className="candidate-document-name" title={doc.DocumentName || doc.FileNameOriginal}>
                            {truncateText(doc.DocumentName || doc.FileNameOriginal)}
                          </div>
                          <div className="candidate-document-meta">
                            {doc.DocumentType && <span>Type: {doc.DocumentType}</span>}
                            {doc.FileSizeBytes && <span>Size: {formatFileSize(doc.FileSizeBytes)}</span>}
                            {doc.FileExtension && <span>Format: {doc.FileExtension.toUpperCase().replace('.', '')}</span>}
                          </div>
                        </div>
                        <div className="candidate-document-actions" style={{ display: 'flex', gap: '8px' }}>
                          <button
                            className="candidate-document-btn"
                            onClick={() => setSelectedResumeId(selectedResumeId === doc.DocumentId ? null : doc.DocumentId)}
                            title={selectedResumeId === doc.DocumentId ? "Close Preview" : "Preview Document"}
                            style={{
                              background: selectedResumeId === doc.DocumentId ? '#3b5bfd' : 'transparent',
                              color: selectedResumeId === doc.DocumentId ? '#fff' : 'inherit'
                            }}
                          >
                            <Eye size={14} /> Preview
                          </button>
                          <button
                            className="candidate-document-btn"
                            onClick={() => handleDownload(doc.DocumentId, doc.FileNameOriginal)}
                          >
                            <Download size={14} /> Download
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '60px 20px', color: '#9ca3af' }}>
                    <FileText size={56} style={{ marginBottom: 14, opacity: 0.35 }} />
                    <p style={{ margin: 0, fontSize: 15 }}>No additional documents available</p>
                  </div>
                )
              )}
            </div>

            {selectedResumeId && (
              <div style={{ flex: '1', minWidth: '400px', height: '800px', border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden', background: '#fff' }}>
                <div style={{ background: '#f9fafb', padding: '10px 16px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontWeight: 600, color: '#374151', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Eye size={16} color="#3b5bfd" />
                    Preview: {truncateText(c.Documents?.find(d => d.DocumentId === selectedResumeId)?.DocumentName || c.Documents?.find(d => d.DocumentId === selectedResumeId)?.FileNameOriginal || 'Document')}
                  </span>
                  <button
                    onClick={() => setSelectedResumeId(null)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280' }}
                  >
                    <X size={18} />
                  </button>
                </div>
                <iframe
                  src={`${BASE_URL}/api/resumes/${selectedResumeId}/preview?token=${localStorage.getItem('token')}`}
                  style={{ width: '100%', height: 'calc(100% - 44px)', border: 'none' }}
                  title="Document Preview"
                />
              </div>
            )}
          </div>
        )}

        {activeTab === 'notes' && (
          <div style={{ background: '#fff', borderRadius: '8px', padding: '24px', border: '1px solid #e5e7eb' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
              <h3 style={{ fontSize: 18, color: '#111', margin: 0 }}>Candidate Notes</h3>
              {!isAddingNote && (
                <button
                  onClick={() => setIsAddingNote(true)}
                  style={{ display: 'flex', alignItems: 'center', gap: '6px', background: '#3b5bfd', color: '#fff', border: 'none', padding: '8px 16px', borderRadius: '4px', cursor: 'pointer' }}
                >
                  <Plus size={16} /> Add Note
                </button>
              )}
            </div>

            {isAddingNote && (
              <div style={{ marginBottom: '24px', background: '#f9fafb', padding: '16px', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                <div style={{ marginBottom: '12px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>Category</label>
                  <select
                    value={newNoteCategory}
                    onChange={(e) => setNewNoteCategory(e.target.value)}
                    style={{ padding: '8px 12px', borderRadius: '4px', border: '1px solid #d1d5db', background: '#fff', fontSize: '14px', width: '220px' }}
                  >
                    {Object.entries(NOTE_CATEGORY_LABELS).map(([val, lbl]) => (
                      <option key={val} value={val}>{lbl}</option>
                    ))}
                  </select>
                </div>
                <textarea
                  value={newNoteText}
                  onChange={(e) => setNewNoteText(e.target.value)}
                  placeholder="Type your note here..."
                  style={{ width: '100%', minHeight: '100px', padding: '12px', borderRadius: '4px', border: '1px solid #d1d5db', marginBottom: '12px', resize: 'vertical' }}
                />
                <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                  <button onClick={() => { setIsAddingNote(false); setNewNoteText(''); setNewNoteCategory('GENERAL'); }} style={{ padding: '8px 16px', background: '#fff', border: '1px solid #d1d5db', borderRadius: '4px', cursor: 'pointer' }}>Cancel</button>
                  <button onClick={handleAddNote} disabled={savingNote} style={{ padding: '8px 16px', background: savingNote ? '#9ca3af' : '#3b5bfd', color: '#fff', border: 'none', borderRadius: '4px', cursor: savingNote ? 'not-allowed' : 'pointer' }}>{savingNote ? 'Saving...' : 'Save Note'}</button>
                </div>
              </div>
            )}

            {notesLoading ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>Loading notes...</div>
            ) : notes.length > 0 ? (
              <div className="erp-notes-timeline-container" style={{ display: 'flex', flexDirection: 'column', gap: '0px' }}>
                {notes.map(note => {
                  const currentUserId = localStorage.getItem('id');
                  const canEditOrDelete = currentUserId && String(currentUserId) === String(note.CreatedByUserId);

                  return (
                    <div key={note.CandidateNoteId} className="erp-notes-timeline-item" style={{ display: 'flex', gap: '10px', padding: '6px 0' }}>
                      {/* Left Column: Name & Date/Time */}
                      <div className="erp-notes-timeline-meta-col" style={{ flex: '0 0 125px', width: '125px', textAlign: 'left', fontSize: '13px', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                        <strong className="erp-notes-timeline-meta-name" style={{ color: '#111827' }}>{note.CreatedByName}</strong>
                        <span className="erp-notes-timeline-meta-date" style={{ color: '#6b7280', fontSize: '12px' }}>
                          {new Date(note.CreatedAtUtc).toLocaleString('en-US', { timeZone: 'UTC' })} UTC
                        </span>
                      </div>

                      {/* Right Column: Note content & Actions */}
                      <div className="erp-notes-timeline-content-col" style={{ flex: 1, paddingLeft: '8px', borderLeft: '1px solid #e5e7eb', position: 'relative', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <div className="erp-notes-timeline-content-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          {(() => {
                            const catStyle = NOTE_CATEGORY_COLORS[note.Category] || NOTE_CATEGORY_COLORS.GENERAL;
                            const catLabel = NOTE_CATEGORY_LABELS[note.Category] || note.Category || 'General';
                            return (
                              <span className="erp-notes-timeline-category-badge" style={{
                                padding: '2px 8px',
                                borderRadius: '12px',
                                fontSize: '11px',
                                fontWeight: '600',
                                backgroundColor: catStyle.bg,
                                color: catStyle.color,
                                border: `1px solid ${catStyle.border}`
                              }}>
                                {catLabel}
                              </span>
                            );
                          })()}

                          {editingNoteId !== note.CandidateNoteId && canEditOrDelete && (
                            <div className="erp-notes-timeline-actions" style={{ display: 'flex', gap: '12px' }}>
                              <button
                                className="erp-notes-timeline-btn-edit"
                                onClick={() => { setEditingNoteId(note.CandidateNoteId); setEditNoteText(note.NoteText); setEditNoteCategory(note.Category || 'GENERAL'); }}
                                style={{ background: 'none', border: 'none', color: '#3b5bfd', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '13px' }}
                              >
                                <Edit size={14} /> Edit
                              </button>
                              <button
                                className="erp-notes-timeline-btn-delete"
                                onClick={() => handleDeleteNote(note.CandidateNoteId)}
                                style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '13px' }}
                              >
                                <Trash2 size={14} /> Delete
                              </button>
                            </div>
                          )}
                        </div>

                        {editingNoteId === note.CandidateNoteId ? (
                          <div>
                            <div style={{ marginBottom: '12px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                              <label style={{ fontSize: '12px', fontWeight: '600', color: '#374151' }}>Category</label>
                              <select
                                value={editNoteCategory}
                                onChange={(e) => setEditNoteCategory(e.target.value)}
                                style={{ padding: '6px 10px', borderRadius: '4px', border: '1px solid #d1d5db', background: '#fff', fontSize: '13px', width: '200px' }}
                              >
                                {Object.entries(NOTE_CATEGORY_LABELS).map(([val, lbl]) => (
                                  <option key={val} value={val}>{lbl}</option>
                                ))}
                              </select>
                            </div>
                            <textarea
                              value={editNoteText}
                              onChange={(e) => setEditNoteText(e.target.value)}
                              style={{ width: '100%', minHeight: '80px', padding: '12px', borderRadius: '4px', border: '1px solid #d1d5db', marginBottom: '12px' }}
                            />
                            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                              <button onClick={() => { setEditingNoteId(null); setEditNoteText(''); setEditNoteCategory('GENERAL'); }} style={{ padding: '6px 12px', fontSize: '13px', background: '#fff', border: '1px solid #d1d5db', borderRadius: '4px', cursor: 'pointer' }}>Cancel</button>
                              <button onClick={() => handleEditNoteSave(note.CandidateNoteId)} disabled={savingNote} style={{ padding: '6px 12px', fontSize: '13px', background: savingNote ? '#9ca3af' : '#3b5bfd', color: '#fff', border: 'none', borderRadius: '4px', cursor: savingNote ? 'not-allowed' : 'pointer' }}>{savingNote ? 'Saving...' : 'Save'}</button>
                            </div>
                          </div>
                        ) : (
                          <div className="erp-notes-timeline-text" style={{ whiteSpace: 'pre-wrap', color: '#111', lineHeight: '1.5' }}>
                            {note.NoteText}
                          </div>
                        )}

                        {note.UpdatedAtUtc && (
                          <div className="erp-notes-timeline-audit" style={{ fontSize: '11px', fontStyle: 'italic', color: '#6b7280', marginTop: '4px' }}>
                            Edited — last updated by {note.UpdatedByName || 'Unknown'} at {new Date(note.UpdatedAtUtc).toLocaleString('en-US', { timeZone: 'UTC' })} UTC
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '40px', color: '#9ca3af', border: '1px dashed #d1d5db', borderRadius: '8px' }}>
                <p>No notes have been added yet.</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'submissions' && (
          <div style={{ background: '#fff', borderRadius: '8px', padding: '24px', border: '1px solid #e5e7eb' }}>
            {/* Header row */}
            <div style={{ marginBottom: '16px' }}>
              <h3 style={{ fontSize: 18, color: '#111', margin: '0 0 4px 0', fontWeight: 600 }}>
                Candidate Submissions
              </h3>
              <p style={{ margin: 0, fontSize: '13px', color: '#64748b' }}>
                Track all job roles and client pipelines where this candidate has been submitted.
              </p>
            </div>

            {/* Date Filters Section */}
            <div style={{
              background: '#f8fafc',
              borderRadius: '8px',
              padding: '12px 16px',
              marginBottom: '20px',
              border: '1px solid #e2e8f0',
              display: 'flex',
              alignItems: 'flex-end',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: '12px'
            }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <span style={{ fontSize: '13px', color: '#64748b', fontWeight: '500' }}>Date filters:</span>
                <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <label style={{ fontSize: '12px', color: '#475569', fontWeight: '500' }}>Quick Select</label>
                    <select
                      value={dateFilters.quickSelect}
                      onChange={handleQuickSelectChange}
                      style={{
                        padding: '7px 12px',
                        borderRadius: '6px',
                        border: '1px solid #cbd5e1',
                        outline: 'none',
                        fontSize: '13px',
                        minWidth: '130px',
                        color: '#334155',
                        background: '#fff'
                      }}
                    >
                      <option value="All Time">All Time</option>
                      <option value="Today">Today</option>
                      <option value="Yesterday">Yesterday</option>
                      <option value="This Week">This Week</option>
                      <option value="Last Week">Last Week</option>
                      <option value="This Month">This Month</option>
                      <option value="Last Month">Last Month</option>
                      <option value="This Quarter">This Quarter</option>
                      <option value="Last Quarter">Last Quarter</option>
                      <option value="Custom">Custom</option>
                    </select>
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <label style={{ fontSize: '12px', color: '#475569', fontWeight: '500' }}>Start date</label>
                    <input
                      type="date"
                      value={dateFilters.startDate}
                      onChange={(e) => setDateFilters(prev => ({ ...prev, startDate: e.target.value, quickSelect: 'Custom' }))}
                      style={{
                        padding: '6px 10px',
                        borderRadius: '6px',
                        border: '1px solid #cbd5e1',
                        outline: 'none',
                        fontSize: '13px',
                        color: '#334155',
                        background: '#fff'
                      }}
                    />
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <label style={{ fontSize: '12px', color: '#475569', fontWeight: '500' }}>End date</label>
                    <input
                      type="date"
                      value={dateFilters.endDate}
                      onChange={(e) => setDateFilters(prev => ({ ...prev, endDate: e.target.value, quickSelect: 'Custom' }))}
                      style={{
                        padding: '6px 10px',
                        borderRadius: '6px',
                        border: '1px solid #cbd5e1',
                        outline: 'none',
                        fontSize: '13px',
                        color: '#334155',
                        background: '#fff'
                      }}
                    />
                  </div>

                  <button
                    type="button"
                    onClick={() => {
                      setAppliedDateFilters({
                        startDate: dateFilters.startDate,
                        endDate: dateFilters.endDate
                      });
                      fetchSubmissions(dateFilters.startDate, dateFilters.endDate);
                    }}
                    style={{
                      padding: '7px 18px',
                      borderRadius: '6px',
                      border: '1px solid #3b5bfd',
                      background: '#eff6ff',
                      color: '#3b5bfd',
                      fontWeight: '600',
                      fontSize: '13px',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      height: '35px'
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = '#3b5bfd'; e.currentTarget.style.color = '#fff'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = '#eff6ff'; e.currentTarget.style.color = '#3b5bfd'; }}
                  >
                    Apply
                  </button>

                  {(appliedDateFilters.startDate || appliedDateFilters.endDate || dateFilters.quickSelect !== 'All Time') && (
                    <button
                      type="button"
                      onClick={() => {
                        setDateFilters({ quickSelect: 'All Time', startDate: '', endDate: '' });
                        setAppliedDateFilters({ startDate: '', endDate: '' });
                        fetchSubmissions('', '');
                      }}
                      style={{
                        padding: '7px 12px',
                        borderRadius: '6px',
                        border: '1px solid #d1d5db',
                        background: '#fff',
                        color: '#6b7280',
                        fontSize: '13px',
                        cursor: 'pointer',
                        height: '35px'
                      }}
                    >
                      Reset
                    </button>
                  )}
                </div>
              </div>

              <div style={{ fontSize: '12px', color: '#64748b' }}>
                Showing <strong>{submissions.length}</strong> {submissions.length === 1 ? 'submission' : 'submissions'}
              </div>
            </div>

            {/* Submissions Table / Content */}
            {submissionsLoading ? (
              <div style={{ textAlign: 'center', padding: '60px', color: '#6b7280' }}>
                <div style={{
                  display: 'inline-block',
                  width: '30px',
                  height: '30px',
                  border: '3px solid #e2e8f0',
                  borderTopColor: '#3b5bfd',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }} />
                <p style={{ marginTop: '12px', fontSize: '14px' }}>Loading submissions...</p>
              </div>
            ) : submissions.length > 0 ? (
              <div style={{ overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', textAlign: 'left' }}>
                  <thead>
                    <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', color: '#475569', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      <th style={{ padding: '12px 14px', fontWeight: 600 }}>Client</th>
                      <th style={{ padding: '12px 14px', fontWeight: 600 }}>Job ID</th>
                      <th style={{ padding: '12px 14px', fontWeight: 600 }}>External Job Ref</th>
                      <th style={{ padding: '12px 14px', fontWeight: 600 }}>Job Title</th>
                      <th style={{ padding: '12px 14px', fontWeight: 600 }}>Type</th>
                      <th style={{ padding: '12px 14px', fontWeight: 600 }}>Exp</th>
                      <th style={{ padding: '12px 14px', fontWeight: 600 }}>Location</th>
                      <th style={{ padding: '12px 14px', fontWeight: 600 }}>Technical Progress</th>
                      <th style={{ padding: '12px 14px', fontWeight: 600 }}>Submitted By</th>
                      <th style={{ padding: '12px 14px', fontWeight: 600 }}>Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {submissions.map((sub, idx) => {
                      const formatTimeAgo = (d) => {
                        if (!d) return '-';
                        const diff = Math.floor((new Date() - new Date(d)) / 1000 / 60);
                        if (diff < 1) return 'Just now';
                        if (diff < 60) return `${diff}m ago`;
                        const hours = Math.floor(diff / 60);
                        if (hours < 24) return `${hours}h ago`;
                        const days = Math.floor(hours / 24);
                        if (days < 30) return `${days}d ago`;
                        return new Date(d).toLocaleDateString();
                      };

                      const getStageBadgeStyle = (stage, status) => {
                        const s = (stage || status || 'SUBMITTED').toUpperCase();
                        if (s.includes('OFFER') || s.includes('HIRED') || s.includes('PLACED')) {
                          return { bg: '#dcfce7', color: '#15803d', border: '#86efac' };
                        }
                        if (s.includes('HOLD')) {
                          return { bg: '#ede9fe', color: '#6d28d9', border: '#c4b5fd' };
                        }
                        if (s.includes('INTERVIEW') || s.includes('CLIENT') || s.includes('L1') || s.includes('L2')) {
                          return { bg: '#e0f2fe', color: '#0369a1', border: '#7dd3fc' };
                        }
                        if (s.includes('REJECT') || s.includes('WITHDRAWN') || s.includes('DECLINED')) {
                          return { bg: '#fee2e2', color: '#b91c1c', border: '#fca5a5' };
                        }
                        return { bg: '#eff6ff', color: '#1d4ed8', border: '#bfdbfe' };
                      };

                      const badgeStyle = getStageBadgeStyle(sub.CurrentStageCode, sub.SubmissionStatusCode);
                      const displayStage = sub.CurrentStageCode
                        ? sub.CurrentStageCode.replace(/_/g, ' ')
                        : (sub.SubmissionStatusCode || 'Submitted to Client');

                      return (
                        <tr
                          key={sub.SubmissionId || idx}
                          onClick={() => navigate(`/submissions/${sub.JobId}`, { state: { jobInfo: sub } })}
                          style={{
                            borderBottom: '1px solid #f1f5f9',
                            cursor: 'pointer',
                            transition: 'background 0.15s ease'
                          }}
                          onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f8fafc'}
                          onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
                        >
                          <td style={{ padding: '12px 14px', fontWeight: 600, color: '#1e293b' }}>
                            {sub.ClientName || 'Direct Client'}
                          </td>
                          <td style={{ padding: '12px 14px', color: '#64748b', fontSize: '12px' }}>
                            {sub.JobCode || `JOB-${sub.JobId}`}
                          </td>
                          <td style={{ padding: '12px 14px', fontWeight: 500, color: '#3b5bfd' }}>
                            {sub.ExternalJobReference || '-'}
                          </td>
                          <td style={{ padding: '12px 14px' }}>
                            <span style={{ fontWeight: 600, color: '#0f172a' }}>
                              {sub.JobTitle}
                            </span>
                          </td>
                          <td style={{ padding: '12px 14px', color: '#475569' }}>
                            <span style={{ background: '#f1f5f9', color: '#334155', padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 500 }}>
                              {sub.EmploymentType || 'Contract'}
                            </span>
                          </td>
                          <td style={{ padding: '12px 14px', color: '#475569' }}>
                            {sub.Experience || '-'}
                          </td>
                          <td style={{ padding: '12px 14px', color: '#64748b', maxWidth: '160px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={sub.Location}>
                            {sub.Location || 'Remote, US'}
                          </td>
                          <td style={{ padding: '12px 14px' }}>
                            <span style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              padding: '3px 10px',
                              borderRadius: '12px',
                              fontSize: '11px',
                              fontWeight: 600,
                              textTransform: 'capitalize',
                              backgroundColor: badgeStyle.bg,
                              color: badgeStyle.color,
                              border: `1px solid ${badgeStyle.border}`
                            }}>
                              {displayStage}
                            </span>
                          </td>
                          <td style={{ padding: '12px 14px', color: '#334155', fontSize: '12px' }}>
                            {sub.SubmittedByUserName || '-'}
                          </td>
                          <td style={{ padding: '12px 14px', color: '#64748b', fontSize: '12px', whiteSpace: 'nowrap' }}>
                            {formatTimeAgo(sub.CreatedAtUtc)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '50px 20px', background: '#f8fafc', borderRadius: '8px', border: '1px dashed #cbd5e1' }}>
                <Briefcase size={36} color="#94a3b8" style={{ marginBottom: '10px' }} />
                <h4 style={{ margin: '0 0 6px 0', color: '#1e293b', fontSize: '16px' }}>No Job Submissions Found</h4>
                <p style={{ margin: '0', color: '#64748b', fontSize: '13px' }}>
                  {appliedDateFilters.startDate || appliedDateFilters.endDate
                    ? 'No submissions match the selected date filter range.'
                    : 'This candidate has not been submitted to any jobs yet.'}
                </p>
              </div>
            )}
          </div>
        )}

        {/* APPLICATIONS (Careers Portal / ATS) */}
        {activeTab === 'applications' && (
          <div style={{ background: '#fff', borderRadius: '8px', padding: '24px', border: '1px solid #e5e7eb' }}>
            {/* Header row */}
            <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '12px' }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', margin: '0 0 4px 0' }}>
                  <Globe size={20} color="#3b5bfd" />
                  <h3 style={{ fontSize: 18, color: '#111', margin: 0, fontWeight: 600 }}>
                    Candidate Applications (Careers Portal)
                  </h3>
                  <span style={{
                    fontSize: '12px',
                    fontWeight: 600,
                    background: '#eff6ff',
                    color: '#1d4ed8',
                    padding: '2px 8px',
                    borderRadius: '12px',
                    border: '1px solid #bfdbfe'
                  }}>
                    ATS Pipeline
                  </span>
                </div>
                <p style={{ margin: 0, fontSize: '13px', color: '#64748b' }}>
                  Track all job openings and direct applications submitted by this candidate via the Careers Portal.
                </p>
              </div>

              {/* Quick Search in Applications */}
              <div style={{ position: 'relative', minWidth: '240px' }}>
                <Search size={15} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
                <input
                  type="text"
                  placeholder="Filter applications..."
                  value={appSearchTerm}
                  onChange={(e) => setAppSearchTerm(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '7px 12px 7px 32px',
                    borderRadius: '6px',
                    border: '1px solid #cbd5e1',
                    fontSize: '13px',
                    outline: 'none'
                  }}
                />
              </div>
            </div>

            {/* Date Filters Section */}
            <div style={{
              background: '#f8fafc',
              borderRadius: '8px',
              padding: '12px 16px',
              marginBottom: '20px',
              border: '1px solid #e2e8f0',
              display: 'flex',
              alignItems: 'flex-end',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: '12px'
            }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <span style={{ fontSize: '13px', color: '#64748b', fontWeight: '500' }}>Date filters:</span>
                <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <label style={{ fontSize: '12px', color: '#475569', fontWeight: '500' }}>Quick Select</label>
                    <select
                      value={appDateFilters.quickSelect}
                      onChange={handleAppQuickSelectChange}
                      style={{
                        padding: '7px 12px',
                        borderRadius: '6px',
                        border: '1px solid #cbd5e1',
                        outline: 'none',
                        fontSize: '13px',
                        minWidth: '130px',
                        color: '#334155',
                        background: '#fff'
                      }}
                    >
                      <option value="All Time">All Time</option>
                      <option value="Today">Today</option>
                      <option value="Yesterday">Yesterday</option>
                      <option value="This Week">This Week</option>
                      <option value="Last Week">Last Week</option>
                      <option value="This Month">This Month</option>
                      <option value="Last Month">Last Month</option>
                      <option value="This Quarter">This Quarter</option>
                      <option value="Last Quarter">Last Quarter</option>
                      <option value="Custom">Custom</option>
                    </select>
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <label style={{ fontSize: '12px', color: '#475569', fontWeight: '500' }}>Start Date</label>
                    <input
                      type="date"
                      value={appDateFilters.startDate}
                      onChange={(e) => setAppDateFilters(prev => ({ ...prev, startDate: e.target.value, quickSelect: 'Custom' }))}
                      style={{
                        padding: '6px 10px',
                        borderRadius: '6px',
                        border: '1px solid #cbd5e1',
                        outline: 'none',
                        fontSize: '13px',
                        color: '#334155',
                        background: '#fff'
                      }}
                    />
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <label style={{ fontSize: '12px', color: '#475569', fontWeight: '500' }}>End Date</label>
                    <input
                      type="date"
                      value={appDateFilters.endDate}
                      onChange={(e) => setAppDateFilters(prev => ({ ...prev, endDate: e.target.value, quickSelect: 'Custom' }))}
                      style={{
                        padding: '6px 10px',
                        borderRadius: '6px',
                        border: '1px solid #cbd5e1',
                        outline: 'none',
                        fontSize: '13px',
                        color: '#334155',
                        background: '#fff'
                      }}
                    />
                  </div>

                  <button
                    type="button"
                    onClick={() => {
                      setAppliedAppDateFilters({
                        startDate: appDateFilters.startDate,
                        endDate: appDateFilters.endDate
                      });
                      fetchApplications(appDateFilters.startDate, appDateFilters.endDate);
                    }}
                    style={{
                      padding: '7px 16px',
                      background: '#3b5bfd',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '6px',
                      fontSize: '13px',
                      fontWeight: 600,
                      cursor: 'pointer',
                      height: '35px',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}
                  >
                    Apply
                  </button>

                  {(appliedAppDateFilters.startDate || appliedAppDateFilters.endDate || appDateFilters.quickSelect !== 'All Time') && (
                    <button
                      type="button"
                      onClick={() => {
                        setAppDateFilters({ quickSelect: 'All Time', startDate: '', endDate: '' });
                        setAppliedAppDateFilters({ startDate: '', endDate: '' });
                        fetchApplications('', '');
                      }}
                      style={{
                        padding: '7px 14px',
                        background: 'transparent',
                        color: '#64748b',
                        border: '1px solid #cbd5e1',
                        borderRadius: '6px',
                        fontSize: '13px',
                        fontWeight: 500,
                        cursor: 'pointer',
                        height: '35px'
                      }}
                    >
                      Reset
                    </button>
                  )}
                </div>
              </div>

              <div style={{ fontSize: '12px', color: '#64748b' }}>
                Showing <strong>{filteredApplications.length}</strong> {filteredApplications.length === 1 ? 'application' : 'applications'}
              </div>
            </div>

            {/* Applications Table / Content */}
            {applicationsLoading ? (
              <div style={{ textAlign: 'center', padding: '60px', color: '#6b7280' }}>
                <div style={{
                  display: 'inline-block',
                  width: '30px',
                  height: '30px',
                  border: '3px solid #e2e8f0',
                  borderTopColor: '#3b5bfd',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }} />
                <p style={{ marginTop: '12px', fontSize: '14px' }}>Loading applications...</p>
              </div>
            ) : filteredApplications.length > 0 ? (
              <div style={{ overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', textAlign: 'left' }}>
                  <thead>
                    <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', color: '#475569', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      <th style={{ padding: '12px 14px', fontWeight: 600 }}>Client</th>
                      <th style={{ padding: '12px 14px', fontWeight: 600 }}>Job ID / Code</th>
                      <th style={{ padding: '12px 14px', fontWeight: 600 }}>Position / Title</th>
                      <th style={{ padding: '12px 14px', fontWeight: 600 }}>Employment Type</th>
                      <th style={{ padding: '12px 14px', fontWeight: 600 }}>Visa Type</th>
                      <th style={{ padding: '12px 14px', fontWeight: 600 }}>AI Match Score</th>
                      <th style={{ padding: '12px 14px', fontWeight: 600 }}>Applied Date</th>
                      <th style={{ padding: '12px 14px', fontWeight: 600 }}>Status</th>
                      <th style={{ padding: '12px 14px', fontWeight: 600, textAlign: 'center' }}>Resume</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredApplications.map((app, idx) => {
                      const formatTimeAgo = (d) => {
                        if (!d) return '-';
                        const diff = Math.floor((new Date() - new Date(d)) / 1000 / 60);
                        if (diff < 1) return 'Just now';
                        if (diff < 60) return `${diff}m ago`;
                        const hours = Math.floor(diff / 60);
                        if (hours < 24) return `${hours}h ago`;
                        const days = Math.floor(hours / 24);
                        if (days < 30) return `${days}d ago`;
                        return new Date(d).toLocaleDateString();
                      };

                      const scoreVal = typeof app.resume_score === 'number' ? app.resume_score : (app.resume_score ? Number(app.resume_score) : null);
                      const getScoreBadge = (score) => {
                        if (score === null || isNaN(score)) return { bg: '#f1f5f9', color: '#64748b', border: '#cbd5e1', label: 'N/A' };
                        if (score >= 75) return { bg: '#ecfdf5', color: '#047857', border: '#6ee7b7', label: `${score}%` };
                        if (score >= 50) return { bg: '#fffbeb', color: '#b45309', border: '#fcd34d', label: `${score}%` };
                        return { bg: '#fef2f2', color: '#b91c1c', border: '#fca5a5', label: `${score}%` };
                      };
                      const scoreBadge = getScoreBadge(scoreVal);

                      return (
                        <tr
                          key={app.id || idx}
                          style={{
                            borderBottom: '1px solid #f1f5f9',
                            transition: 'background 0.15s ease'
                          }}
                          onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f8fafc'}
                          onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
                        >
                          <td style={{ padding: '12px 14px', fontWeight: 600, color: '#1e293b' }}>
                            {app.ClientName || 'Direct Client'}
                          </td>
                          <td style={{ padding: '12px 14px', color: '#64748b', fontSize: '12px' }}>
                            <span style={{ fontWeight: 600, color: '#2563eb' }}>{app.JobCode || (app.JobId ? `JOB-${app.JobId}` : (app.job_id || '-'))}</span>
                          </td>
                          <td style={{ padding: '12px 14px' }}>
                            <span style={{ fontWeight: 600, color: '#0f172a', display: 'block' }}>
                              {app.JobTitle || app.position || 'Application'}
                            </span>
                            {(app.current_city || app.current_state) && (
                              <span style={{ fontSize: '11px', color: '#64748b', display: 'flex', alignItems: 'center', gap: '3px', marginTop: '2px' }}>
                                <MapPin size={11} /> {[app.current_city, app.current_state].filter(Boolean).join(', ')}
                              </span>
                            )}
                          </td>
                          <td style={{ padding: '12px 14px', color: '#475569' }}>
                            <span style={{
                              background: '#f1f5f9',
                              padding: '2px 8px',
                              borderRadius: '4px',
                              fontSize: '12px',
                              fontWeight: 500
                            }}>
                              {app.employment_type || 'Full-time'}
                            </span>
                          </td>
                          <td style={{ padding: '12px 14px', color: '#475569' }}>
                            <span style={{
                              background: '#eff6ff',
                              color: '#1d4ed8',
                              padding: '2px 8px',
                              borderRadius: '4px',
                              fontSize: '12px',
                              fontWeight: 500,
                              border: '1px solid #dbeafe'
                            }}>
                              {app.visa_type || 'Not Specified'}
                            </span>
                          </td>
                          <td style={{ padding: '12px 14px' }}>
                            <button
                              type="button"
                              onClick={() => {
                                setSelectedAppScoreDetails(app);
                                setShowAppScoreModal(true);
                              }}
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '5px',
                                background: scoreBadge.bg,
                                color: scoreBadge.color,
                                border: `1px solid ${scoreBadge.border}`,
                                borderRadius: '12px',
                                padding: '3px 9px',
                                fontSize: '12px',
                                fontWeight: 700,
                                cursor: 'pointer',
                                transition: 'all 0.15s ease'
                              }}
                              title="Click to view AI match breakdown"
                            >
                              <Sparkles size={12} />
                              {scoreBadge.label}
                            </button>
                          </td>
                          <td style={{ padding: '12px 14px', color: '#334155', whiteSpace: 'nowrap' }}>
                            <div style={{ fontWeight: 500 }}>
                              {app.created_at ? new Date(app.created_at).toLocaleDateString() : '-'}
                            </div>
                            <div style={{ fontSize: '11px', color: '#94a3b8' }}>
                              {formatTimeAgo(app.created_at)}
                            </div>
                          </td>
                          <td style={{ padding: '12px 14px' }}>
                            <span style={{
                              background: '#ecfdf5',
                              color: '#047857',
                              border: '1px solid #a7f3d0',
                              borderRadius: '12px',
                              padding: '2px 8px',
                              fontSize: '11px',
                              fontWeight: 600,
                              textTransform: 'capitalize'
                            }}>
                              {app.status || 'Applied'}
                            </span>
                          </td>
                          <td style={{ padding: '12px 14px', textAlign: 'center' }}>
                            {app.resume_s3_key ? (
                              <button
                                type="button"
                                onClick={() => {
                                  window.open(`${BASE_URL}/api/ats/download-resume/${app.id}`, '_blank');
                                }}
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '4px',
                                  padding: '4px 10px',
                                  borderRadius: '6px',
                                  border: '1px solid #cbd5e1',
                                  background: '#fff',
                                  color: '#3b5bfd',
                                  cursor: 'pointer',
                                  fontSize: '12px',
                                  fontWeight: 500
                                }}
                                title="Download Submitted Resume"
                              >
                                <Download size={13} /> PDF
                              </button>
                            ) : (
                              <span style={{ color: '#94a3b8', fontSize: '12px' }}>-</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '50px 20px', background: '#f8fafc', borderRadius: '8px', border: '1px dashed #cbd5e1' }}>
                <Globe size={36} color="#94a3b8" style={{ marginBottom: '10px' }} />
                <h4 style={{ margin: '0 0 6px 0', color: '#1e293b', fontSize: '16px' }}>No Job Applications Found</h4>
                <p style={{ margin: '0', color: '#64748b', fontSize: '13px' }}>
                  {appSearchTerm || appliedAppDateFilters.startDate || appliedAppDateFilters.endDate
                    ? 'No applications match your current search or date filter range.'
                    : 'This candidate has not applied to any positions through the careers portal yet.'}
                </p>
              </div>
            )}
          </div>
        )}

      </div>

      {/* AI Score Breakdown Modal */}
      {showAppScoreModal && selectedAppScoreDetails && (
        <div
          style={{
            position: 'fixed',
            top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(15, 23, 42, 0.65)',
            backdropFilter: 'blur(4px)',
            zIndex: 10000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px'
          }}
          onClick={(e) => { if (e.target === e.currentTarget) setShowAppScoreModal(false); }}
        >
          <div style={{
            background: '#fff',
            borderRadius: '12px',
            width: '100%',
            maxWidth: '540px',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            overflow: 'hidden'
          }}>
            <div style={{
              padding: '16px 20px',
              borderBottom: '1px solid #e2e8f0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              background: '#f8fafc'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Sparkles size={18} color="#3b5bfd" />
                <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: '#0f172a' }}>
                  AI Resume Match Analysis
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setShowAppScoreModal(false)}
                style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#64748b', padding: '4px' }}
              >
                <X size={18} />
              </button>
            </div>

            <div style={{ padding: '20px' }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '12px 16px',
                background: '#eff6ff',
                border: '1px solid #bfdbfe',
                borderRadius: '8px',
                marginBottom: '16px'
              }}>
                <div>
                  <div style={{ fontSize: '14px', fontWeight: 600, color: '#0f172a' }}>
                    {selectedAppScoreDetails.JobTitle || selectedAppScoreDetails.position || 'Target Position'}
                  </div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>
                    {selectedAppScoreDetails.ClientName || 'Direct Client'} • {selectedAppScoreDetails.JobCode || `JOB-${selectedAppScoreDetails.JobId || selectedAppScoreDetails.id}`}
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <span style={{
                    fontSize: '22px',
                    fontWeight: 700,
                    color: '#2563eb'
                  }}>
                    {selectedAppScoreDetails.resume_score ?? 'N/A'}{typeof selectedAppScoreDetails.resume_score === 'number' || !isNaN(Number(selectedAppScoreDetails.resume_score)) ? '%' : ''}
                  </span>
                  <div style={{ fontSize: '11px', color: '#64748b', fontWeight: 500 }}>
                    Match Score
                  </div>
                </div>
              </div>

              {/* Matched Skills */}
              <div style={{ marginBottom: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
                  <CheckCircle size={15} color="#16a34a" />
                  <span style={{ fontSize: '13px', fontWeight: 600, color: '#166534' }}>
                    Matched Skills ({Array.isArray(selectedAppScoreDetails.matched_skills) ? selectedAppScoreDetails.matched_skills.length : 0})
                  </span>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  {Array.isArray(selectedAppScoreDetails.matched_skills) && selectedAppScoreDetails.matched_skills.length > 0 ? (
                    selectedAppScoreDetails.matched_skills.map((skill, sIdx) => (
                      <span key={sIdx} style={{
                        background: '#dcfce7',
                        color: '#15803d',
                        border: '1px solid #86efac',
                        borderRadius: '12px',
                        padding: '3px 10px',
                        fontSize: '12px',
                        fontWeight: 500
                      }}>
                        ✓ {skill}
                      </span>
                    ))
                  ) : (
                    <span style={{ fontSize: '12px', color: '#94a3b8' }}>None identified</span>
                  )}
                </div>
              </div>

              {/* Missing Skills */}
              <div style={{ marginBottom: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
                  <XCircle size={15} color="#dc2626" />
                  <span style={{ fontSize: '13px', fontWeight: 600, color: '#991b1b' }}>
                    Missing Skills ({Array.isArray(selectedAppScoreDetails.missing_skills) ? selectedAppScoreDetails.missing_skills.length : 0})
                  </span>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  {Array.isArray(selectedAppScoreDetails.missing_skills) && selectedAppScoreDetails.missing_skills.length > 0 ? (
                    selectedAppScoreDetails.missing_skills.map((skill, sIdx) => (
                      <span key={sIdx} style={{
                        background: '#fee2e2',
                        color: '#b91c1c',
                        border: '1px solid #fca5a5',
                        borderRadius: '12px',
                        padding: '3px 10px',
                        fontSize: '12px',
                        fontWeight: 500
                      }}>
                        ✗ {skill}
                      </span>
                    ))
                  ) : (
                    <span style={{ fontSize: '12px', color: '#94a3b8' }}>None identified</span>
                  )}
                </div>
              </div>
            </div>

            <div style={{ padding: '12px 20px', background: '#f8fafc', borderTop: '1px solid #e2e8f0', textAlign: 'right' }}>
              <button
                type="button"
                onClick={() => setShowAppScoreModal(false)}
                style={{
                  padding: '6px 18px',
                  background: '#3b5bfd',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: 600,
                  fontSize: '13px'
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <SubmitProfileModal
        isOpen={showSubmitModal}
        onClose={() => setShowSubmitModal(false)}
        candidate={c}
        onBulkSuccess={() => {
          fetchSubmissions();
        }}
      />
    </div>
  );

  if (isModal) {
    return (
      <div
        className="onb-candidate-modal-overlay"
        onClick={(e) => { if (e.target === e.currentTarget && onClose) onClose(); }}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(15, 23, 42, 0.65)',
          backdropFilter: 'blur(4px)',
          zIndex: 9999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '24px'
        }}
      >
        <div
          className="onb-candidate-modal-dialog"
          style={{
            backgroundColor: '#fff',
            borderRadius: '12px',
            width: '95%',
            maxWidth: '1440px',
            maxHeight: '92vh',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            overflow: 'hidden'
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '14px 24px',
              borderBottom: '1px solid #e2e8f0',
              backgroundColor: '#f8fafc'
            }}
          >
            <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700, color: '#0f172a' }}>
              Candidate Quick Overview
            </h3>
            <button
              type="button"
              onClick={onClose}
              style={{
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                color: '#64748b',
                fontSize: '0.88rem',
                fontWeight: 600
              }}
            >
              <X size={18} /> Close
            </button>
          </div>
          <div style={{ overflowY: 'auto', flex: 1 }}>
            {content}
          </div>
        </div>
      </div>
    );
  }

  return content;
};

export default CandidateDetail;